/**
 * Detect enclosed regions bounded by walls/arcs and bin perimeter arcs.
 */
import {
    circleFromThreePoints,
    closedRectIdForFace,
    getMarkerRadiusImagePx,
    PIXEL_TO_FEET_DEFAULT,
    isFloatingSegment,
    quantizeBinSnapAngle,
    resolveSegmentGeometry,
    resolveEndpoint,
} from "./siteLayoutStice.js";

/**
 * Rounded image-pixel key — every graph vertex (bin perimeter angle, wall endpoint,
 * wall midpoint junction) is identified by its coordinate so geometrically coincident
 * points share a single graph vertex.
 */
const coordKey = (x, y) => `p:${Math.round(x)},${Math.round(y)}`;

const JUNCTION_TOL_PX = 4;
/** External tangency between round-bin circles (image px). */
const BIN_TOUCH_TOL_PX = 5;
/**
 * Two bins are "snapped together" when their perimeters meet within this tolerance.
 * Kept tight so non-touching bins never get a phantom tangent contact angle, which
 * would otherwise produce ghost enclosed areas.
 */
const BIN_SNAP_ADJACENT_TOL_PX = 8;
/** Above this, skip planar face walk (prevents OOM on large layouts). Arc pockets still synthesized. */
const MAX_PLANAR_EDGES = 5000;
const MAX_ENCLOSURE_FACES = 150;

/**
 * Skip canvas name tags only when the area count is very large (sidebar still lists them).
 * Tags are drawn only while idle (not interacting), so this can match the enclosure cap
 * and keep labels visible for normal multi-bin grids that synthesize many gap pockets.
 */
export const LAYOUT_CANVAS_TAG_FACE_LIMIT = 150;

/** Above this marker count, skip planar graph walk (synthesis-only — much faster). */
export const FAST_LAYOUT_MARKER_LIMIT = 18;

/** With this many markers + segments combined, use synthesis-only enclosure. */
export const LAYOUT_HEAVY_OBJECT_LIMIT = 12;

/**
 * A wall/arc network of at least this size encloses arbitrary regions that only the
 * planar graph can fully enumerate (synthesis only covers a fixed catalog of pockets).
 * A closed rectangle boundary is exactly 4 walls, so 4 is the minimum enclosing loop.
 */
export const LAYOUT_SEGMENT_GRAPH_LIMIT = 4;

export const shouldUseFastEnclosedFaces = (markers, segments) => {
    const m = markers?.length ?? 0;
    const s = segments?.length ?? 0;
    // Real wall/arc networks enclose arbitrary regions that only the planar graph can
    // enumerate (synthesis covers a fixed catalog of pockets). Use the graph whenever
    // such a network exists, regardless of bin count; the MAX_PLANAR_EDGES guard inside
    // the walk falls back to synthesis if the graph is pathologically large.
    if (s >= LAYOUT_SEGMENT_GRAPH_LIMIT) return false;
    // No real segment network: very large bin counts make per-bin perimeter
    // subdivision too heavy, so synthesis-only regardless.
    if (m > FAST_LAYOUT_MARKER_LIMIT) return true;
    // Mostly-bin grids with few/no segments: synthesis is complete and much faster.
    return m + s >= LAYOUT_HEAVY_OBJECT_LIMIT;
};

let enclosureCacheKey = "";
let enclosureCacheResult = [];

const mixCache = (h, n) => {
    let x = h ^ (n | 0);
    return Math.imul(x, 16777619) >>> 0;
};

/** Fast geometry fingerprint — skips redundant computeEnclosedFaces on unchanged layout. */
export const buildLayoutGeometryCacheKey = (markers, segments) => {
    let h = 2166136261;
    h = mixCache(h, (markers || []).length);
    (markers || []).forEach((m) => {
        h = mixCache(h, Math.round((m.x ?? 0) * 4));
        h = mixCache(h, Math.round((m.y ?? 0) * 4));
        h = mixCache(h, Math.round((m.radiusPx ?? 0) * 4));
        const bid = m.binDataId != null ? String(m.binDataId) : "";
        for (let i = 0; i < bid.length; i++) h = mixCache(h, bid.charCodeAt(i));
    });
    const segs = segments || [];
    h = mixCache(h, segs.length);
    segs.forEach((s) => {
        h = mixCache(h, Math.round((s.x1 ?? 0) * 2));
        h = mixCache(h, Math.round((s.y1 ?? 0) * 2));
        h = mixCache(h, Math.round((s.x2 ?? 0) * 2));
        h = mixCache(h, Math.round((s.y2 ?? 0) * 2));
        h = mixCache(h, Math.round((s.curveX ?? s.bulgeX ?? 0) * 2));
        h = mixCache(h, Math.round((s.curveY ?? s.bulgeY ?? 0) * 2));
        h = mixCache(h, Math.round((s.angle1 ?? 0) * 100));
        h = mixCache(h, Math.round((s.angle2 ?? 0) * 100));
        const mid1 = s.markerId1 != null ? String(s.markerId1) : "";
        const mid2 = s.markerId2 != null ? String(s.markerId2) : "";
        for (let i = 0; i < mid1.length; i++) h = mixCache(h, mid1.charCodeAt(i));
        for (let i = 0; i < mid2.length; i++) h = mixCache(h, mid2.charCodeAt(i));
        const id = s.id != null ? String(s.id) : "";
        for (let i = 0; i < id.length; i++) h = mixCache(h, id.charCodeAt(i));
    });
    return `${(markers || []).length}:${segs.length}:${h}`;
};

export const invalidateEnclosureCache = () => {
    enclosureCacheKey = "";
    enclosureCacheResult = [];
};

const decimatePolygonForDraw = (poly, maxPts = 36) => {
    if (!poly?.length || poly.length <= maxPts) return poly;
    const out = [];
    const n = poly.length;
    for (let i = 0; i < maxPts; i++) {
        out.push(poly[Math.floor((i * n) / maxPts)]);
    }
    return out;
};

const distToSegment = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-6) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

/** Point on wall interior (not at endpoints) — for T-junction / midpoint splits. */
const pointOnWallInterior = (px, py, x1, y1, x2, y2, tol = JUNCTION_TOL_PX) => {
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (len < 1e-6) return false;
    if (Math.hypot(px - x1, py - y1) <= tol || Math.hypot(px - x2, py - y2) <= tol) return false;
    return distToSegment(px, py, x1, y1, x2, y2) <= tol;
};

const dedupeNearbyPoints = (points, tol = JUNCTION_TOL_PX) => {
    const out = [];
    points.forEach((p) => {
        if (!out.some((q) => Math.hypot(q.x - p.x, q.y - p.y) <= tol)) out.push(p);
    });
    return out;
};

const sortPointsAlongWall = (points, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    const t = (p) =>
        len2 < 1e-6 ? 0 : ((p.x - x1) * dx + (p.y - y1) * dy) / len2;
    return [...points].sort((a, b) => t(a) - t(b));
};

/** Distance from a point to a user-drawn arc polyline (not the straight chord). */
const distToUserArc = (geom, px, py) => {
    const pts = sampleUserArc(geom, 16);
    let best = Infinity;
    for (let i = 0; i < pts.length - 1; i++) {
        best = Math.min(
            best,
            distToSegment(px, py, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y)
        );
    }
    return best;
};

/** True when `test` lies on the CCW arc from `start` to `end` (circle angles). */
const angleInCCWArc = (start, end, test) => {
    let de = end - start;
    while (de <= 0) de += 2 * Math.PI;
    let dt = test - start;
    while (dt < 0) dt += 2 * Math.PI;
    while (dt >= 2 * Math.PI) dt -= 2 * Math.PI;
    return dt <= de + 1e-9;
};

/** Parameter 0→1 along the user arc from start to end. */
const arcParamAlong = (geom, x, y) => {
    const circle = circleFromThreePoints(
        geom.x1,
        geom.y1,
        geom.x2,
        geom.y2,
        geom.curveX,
        geom.curveY
    );
    if (!circle) return 0;
    const start = Math.atan2(geom.y1 - circle.cy, geom.x1 - circle.cx);
    const end = Math.atan2(geom.y2 - circle.cy, geom.x2 - circle.cx);
    const bulge = Math.atan2(geom.curveY - circle.cy, geom.curveX - circle.cx);
    const a = Math.atan2(y - circle.cy, x - circle.cx);
    let ccw = end - start;
    while (ccw <= 0) ccw += 2 * Math.PI;
    const cw = ccw - 2 * Math.PI;
    const delta = angleInCCWArc(start, end, bulge) ? ccw : cw;
    let t = a - start;
    if (delta < 0) {
        while (t > 0) t -= 2 * Math.PI;
        while (t <= delta) t += 2 * Math.PI;
    } else {
        while (t < 0) t += 2 * Math.PI;
        while (t > delta) t -= 2 * Math.PI;
    }
    return t / (delta || 1);
};

/** Insert junction vertices into an arc polyline, ordered along the curve (not the chord). */
const arcPolylineWithJunctions = (geom, junctions) => {
    const start = {
        x: geom.x1,
        y: geom.y1,
        markerId: geom.markerId1,
        angle: geom.angle1,
    };
    const end = {
        x: geom.x2,
        y: geom.y2,
        markerId: geom.markerId2,
        angle: geom.angle2,
    };
    const interior = sampleUserArc(geom, 12)
        .slice(1, -1)
        .map((p) => ({ x: p.x, y: p.y, markerId: null, angle: null }));
    const onArc = junctions
        .filter((j) => distToUserArc(geom, j.x, j.y) <= JUNCTION_TOL_PX)
        .map((j) => ({ x: j.x, y: j.y, markerId: null, angle: null }));
    const merged = dedupeNearbyPoints([start, ...interior, ...onArc, end], JUNCTION_TOL_PX);
    merged.sort((a, b) => arcParamAlong(geom, a.x, a.y) - arcParamAlong(geom, b.x, b.y));
    return merged;
};

const normalizeAngle = (a) => {
    let t = a;
    while (t <= -Math.PI) t += 2 * Math.PI;
    while (t > Math.PI) t -= 2 * Math.PI;
    return t;
};

/** Bin perimeter tolerance (px) for inferring snap angles from coordinates. */
const binPerimeterTolPx = (marker) =>
    Math.max(4, getMarkerRadiusImagePx(marker) * 0.06);

/** If (x,y) lies on a bin circle, return { markerId, angle }. */
const inferBinSnap = (x, y, markers) => {
    let best = null;
    let bestErr = Infinity;
    (markers || []).forEach((m) => {
        const r = getMarkerRadiusImagePx(m);
        const dist = Math.hypot(x - m.x, y - m.y);
        const err = Math.abs(dist - r);
        const tol = binPerimeterTolPx(m);
        if (err <= tol && err < bestErr) {
            bestErr = err;
            best = {
                markerId: m.id,
                angle: Math.atan2(y - m.y, x - m.x),
            };
        }
    });
    return best;
};

/** Wall must touch the bin perimeter within this gap to count as a contact (px). */
const wallBinContactTolPx = (marker) =>
    Math.max(10, getMarkerRadiusImagePx(marker) * 0.2);

/**
 * Points where a straight wall runs tangent to (or crosses) a bin perimeter, returned on
 * the exact circle perimeter so the planar graph shares one vertex between the wall split
 * and the bin sub-arc. Lets regions bounded by a wall + bin arcs (e.g. gaps between bins
 * and an outer boundary rectangle) close into enclosed faces. Endpoints are excluded —
 * wall→bin snaps at endpoints are already handled by segment geometry.
 *
 * The tolerance is forgiving (boundary rectangles are placed by hand and rarely sit pixel-
 * exact on every bin tangent); a near-tangent wall still snaps to the bin perimeter point.
 */
const wallSegmentBinContacts = (geom, marker) => {
    const r = getMarkerRadiusImagePx(marker);
    if (r < 4) return [];
    const tol = wallBinContactTolPx(marker);
    const ax = geom.x1;
    const ay = geom.y1;
    const bx = geom.x2;
    const by = geom.y2;
    if (![ax, ay, bx, by].every(Number.isFinite)) return [];
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-6) return [];
    const len = Math.sqrt(len2);
    const tC = ((marker.x - ax) * dx + (marker.y - ay) * dy) / len2;
    const footX = ax + tC * dx;
    const footY = ay + tC * dy;
    const h = Math.hypot(footX - marker.x, footY - marker.y);
    if (h > r + tol) return [];
    const out = [];
    const interior = (t) => t >= 0.005 && t <= 0.995;
    const perimeterPoint = (px, py) => {
        const a = Math.atan2(py - marker.y, px - marker.x);
        return {
            x: marker.x + r * Math.cos(a),
            y: marker.y + r * Math.sin(a),
            angle: a,
        };
    };
    if (h >= r - tol) {
        // Tangent (or near-tangent): one contact at the foot, projected to the perimeter.
        if (interior(tC)) out.push(perimeterPoint(footX, footY));
        return out;
    }
    // Crossing: the wall passes through the disk — two perimeter intersection points.
    const halfLen = Math.sqrt(r * r - h * h);
    const dt = halfLen / len;
    [tC - dt, tC + dt].forEach((t) => {
        if (!interior(t)) return;
        out.push(perimeterPoint(ax + t * dx, ay + t * dy));
    });
    return out;
};

/**
 * Sample the circular arc through start, bulge, and end (image px).
 * Uses the bulge point — not isInner — so enclosure matches the curve the user drew.
 */
const sampleUserArc = (geom, steps = 10) => {
    const circle = circleFromThreePoints(geom.x1, geom.y1, geom.x2, geom.y2, geom.curveX, geom.curveY);
    if (!circle) {
        return [
            { x: geom.x1, y: geom.y1 },
            { x: geom.x2, y: geom.y2 },
        ];
    }
    const start = Math.atan2(geom.y1 - circle.cy, geom.x1 - circle.cx);
    const end = Math.atan2(geom.y2 - circle.cy, geom.x2 - circle.cx);
    const bulge = Math.atan2(geom.curveY - circle.cy, geom.curveX - circle.cx);
    let ccw = end - start;
    while (ccw <= 0) ccw += 2 * Math.PI;
    const cw = ccw - 2 * Math.PI;
    const delta = angleInCCWArc(start, end, bulge) ? ccw : cw;
    const pts = [];
    for (let i = 0; i <= steps; i++) {
        const t = start + (delta * i) / steps;
        pts.push({
            x: circle.cx + circle.r * Math.cos(t),
            y: circle.cy + circle.r * Math.sin(t),
        });
    }
    return pts;
};

/** Bin perimeter leg that runs along the side nearest the arc bulge (exterior pocket). */
const chooseBinLegToward = (marker, fromAngle, toAngle, bulgeX, bulgeY, steps = 16) => {
    const ccw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, true);
    const cw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, false);
    const mid = (path) => path[Math.floor(path.length / 2)];
    const mc = mid(ccw);
    const mw = mid(cw);
    const dc = Math.hypot(mc.x - bulgeX, mc.y - bulgeY);
    const dw = Math.hypot(mw.x - bulgeX, mw.y - bulgeY);
    return dc <= dw ? ccw : cw;
};

/** Score how many samples on a perimeter path sit on the arc bulge (exterior) side of the chord. */
const scorePathOnBulgeSide = (path, geom) => {
    const bulgeSide = bulgeSideOfChord(geom, geom.curveX, geom.curveY);
    if (Math.abs(bulgeSide) < 1e-3) return 0;
    let score = 0;
    for (const p of path) {
        const s = bulgeSideOfChord(geom, p.x, p.y);
        if (Math.sign(s) === Math.sign(bulgeSide)) score += 1;
        else if (Math.abs(s) > 1) score -= 1;
    }
    return score;
};

/** Perimeter leg on the same side of the arc chord as the bulge (for synthesized pockets). */
const pickBinLegOnBulgeSide = (marker, fromAngle, toAngle, geom, steps = 16) => {
    const ccw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, true);
    const cw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, false);
    const bulgeSide = bulgeSideOfChord(geom, geom.curveX, geom.curveY);
    if (Math.abs(bulgeSide) < 1e-3) {
        return chooseBinLegToward(marker, fromAngle, toAngle, geom.curveX, geom.curveY, steps);
    }
    return scorePathOnBulgeSide(ccw, geom) >= scorePathOnBulgeSide(cw, geom) ? ccw : cw;
};

/** Perimeter leg between arc and wall attach — exterior side only (not through bin interior). */
const pickBinLegAlongBulgeSide = (marker, fromAngle, toAngle, geom, steps = 20) =>
    pickBinLegOnBulgeSide(marker, fromAngle, toAngle, geom, steps);

/**
 * Pick the perimeter leg whose midpoint faces the partner bin (the side of marker
 * that touches the pocket area). Used for wall+arc pockets where the bulge-side
 * scoring is ambiguous (e.g. arcs whose chord runs parallel to bin-center vector).
 */
const pickPerimeterLegTowardPartner = (marker, fromAngle, toAngle, partner, steps = 16) => {
    const ccw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, true);
    const cw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, false);
    const partnerDir = Math.atan2(partner.y - marker.y, partner.x - marker.x);
    const midAngle = (path) => {
        const mid = path[Math.floor(path.length / 2)];
        return Math.atan2(mid.y - marker.y, mid.x - marker.x);
    };
    const angleDelta = (a, b) => {
        let d = Math.abs(normalizeAngle(a) - normalizeAngle(b));
        if (d > Math.PI) d = 2 * Math.PI - d;
        return d;
    };
    return angleDelta(midAngle(ccw), partnerDir) <=
        angleDelta(midAngle(cw), partnerDir)
        ? ccw
        : cw;
};

/** True when two round bins are externally tangent (image px). */
const binsExternallyTouch = (m1, m2) => {
    const r1 = getMarkerRadiusImagePx(m1);
    const r2 = getMarkerRadiusImagePx(m2);
    const d = Math.hypot(m2.x - m1.x, m2.y - m1.y);
    if (d < 1e-3) return false;
    if (d > r1 + r2 + BIN_TOUCH_TOL_PX) return false;
    if (d < Math.abs(r1 - r2) - BIN_TOUCH_TOL_PX) return false;
    return true;
};

/**
 * True when two bin perimeters meet externally (touch or near-touch).
 * A pair counts as "snapped" only when the centre distance is close to r1+r2;
 * any looser test invents tangent contact angles for bins that visually do not touch.
 */
const binsAreSnappedTogether = (m1, m2) => {
    if (binsExternallyTouch(m1, m2)) return true;
    const r1 = getMarkerRadiusImagePx(m1);
    const r2 = getMarkerRadiusImagePx(m2);
    const d = Math.hypot(m2.x - m1.x, m2.y - m1.y);
    if (d < 1e-3) return false;
    const snapTol = Math.max(
        BIN_SNAP_ADJACENT_TOL_PX,
        (r1 + r2) * 0.1
    );
    return Math.abs(d - (r1 + r2)) <= snapTol;
};

/**
 * Register the exact contact-point angle on each bin where two snapped/tangent bins meet.
 * Uses the true geometric direction (not quantized) so the two bins' perimeter sub-arcs
 * end at the exact same image-pixel coordinate, which lets the planar graph share that
 * vertex and detect the inter-bin enclosed regions.
 */
const registerTouchingBinAngles = (markers, registerBinAngle) => {
    for (let i = 0; i < markers.length; i++) {
        for (let j = i + 1; j < markers.length; j++) {
            const m1 = markers[i];
            const m2 = markers[j];
            if (!binsAreSnappedTogether(m1, m2)) continue;

            const dx = m2.x - m1.x;
            const dy = m2.y - m1.y;
            if (Math.hypot(dx, dy) < 1e-3) continue;
            const base = Math.atan2(dy, dx);
            registerBinAngle(m1.id, base);
            registerBinAngle(m2.id, base + Math.PI);
        }
    }
};

/** Second perimeter anchor toward the other bin so arc endpoints get a full bin boundary path. */
const registerArcNeighborAngles = (markers, resolvedById, registerBinAngle) => {
    resolvedById.forEach((geom) => {
        if (geom.kind !== "arc" || geom.markerId1 == null || geom.markerId2 == null) return;
        const m1 = markers.find((m) => m.id === geom.markerId1);
        const m2 = markers.find((m) => m.id === geom.markerId2);
        if (!m1 || !m2) return;
        const dx = m2.x - m1.x;
        const dy = m2.y - m1.y;
        if (Math.hypot(dx, dy) < 1e-3) return;
        registerBinAngle(m1.id, Math.atan2(dy, dx));
        registerBinAngle(m2.id, Math.atan2(-dy, -dx));
    });
};

const pointOnBinPerimeter = (marker, angle) => {
    const r = getMarkerRadiusImagePx(marker);
    const a = normalizeAngle(angle);
    return {
        x: marker.x + r * Math.cos(a),
        y: marker.y + r * Math.sin(a),
    };
};

/** Angle on `marker` toward a snapped partner bin (tangent contact). */
const partnerContactAngle = (marker, partner) =>
    Math.atan2(partner.y - marker.y, partner.x - marker.x);

/** Exterior perimeter leg for arc pockets (dense samples, bulge-side). */
const sampleExteriorBinLeg = (marker, fromAngle, toAngle, geom, steps = 32) =>
    pickBinLegOnBulgeSide(marker, fromAngle, toAngle, geom, steps);

/**
 * Perimeter leg on the pocket side of the arc — average sample stays nearest the bulge.
 * More reliable than chord-side scoring when the arc chord is nearly vertical.
 */
const sampleExteriorBinLegTowardBulge = (marker, fromAngle, toAngle, geom, steps = 40) => {
    const ccw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, true);
    const cw = sampleBinPerimeterBetween(marker, fromAngle, toAngle, steps, false);
    const bx = geom.curveX;
    const by = geom.curveY;
    const avgDist = (path) => {
        let s = 0;
        path.forEach((p) => {
            s += Math.hypot(p.x - bx, p.y - by);
        });
        return s / Math.max(path.length, 1);
    };
    return avgDist(ccw) <= avgDist(cw) ? ccw : cw;
};

const markerIdsFromBinArcKeys = (binArcKeys) => {
    const ids = new Set();
    (binArcKeys || []).forEach((key) => {
        const colon = String(key).indexOf(":");
        if (colon > 0) ids.add(String(key).slice(0, colon));
    });
    return ids;
};

/** Diamond/quad void between four snapped bins (e.g. area H) — not an arc channel fill. */
const isMultiBinCentralGraphPocket = (face, userSegmentIds) => {
    if (faceIdIsSynthesizedPocket(face)) return false;
    const mids = markerIdsFromBinArcKeys(face.binArcKeys);
    if (mids.size >= 4) return true;
    const userSegs = (face.segmentIds || []).filter((id) =>
        userSegmentIds.has(id)
    );
    const binArcs = face.binArcKeys?.length ?? 0;
    return userSegs.length === 0 && binArcs >= 6;
};

/** Midpoint of the exterior bin leg must lie inside the pocket (rejects arc+chord fills). */
const arcPocketCoversExteriorBinLegs = (poly, m1, m2, geom) => {
    if (!poly?.length || !m1 || !m2) return true;
    const c1 = partnerContactAngle(m1, m2);
    const c2 = partnerContactAngle(m2, m1);
    const probes = [
        sampleExteriorBinLegTowardBulge(m2, geom.angle2, c2, geom),
        sampleExteriorBinLegTowardBulge(m1, c1, geom.angle1, geom),
    ];
    // The leg midpoint is a vertex on the polygon boundary, where point-in-polygon
    // is ambiguous and flips with orientation/quadrant. Nudge slightly toward the
    // pocket interior (centroid) so the test is robust on all four sides.
    const cen = polygonCentroid(poly);
    return probes.every((leg) => {
        const mid = leg[Math.floor(leg.length / 2)];
        if (!mid) return false;
        const px = mid.x + (cen.x - mid.x) * 0.05;
        const py = mid.y + (cen.y - mid.y) * 0.05;
        return pointInPolygon(px, py, poly);
    });
};

/** Sample bin perimeter from fromAngle to toAngle (CCW if useCcw). */
const sampleBinPerimeterBetween = (marker, fromAngle, toAngle, steps, useCcw) => {
    const r = getMarkerRadiusImagePx(marker);
    const na1 = normalizeAngle(fromAngle);
    const na2 = normalizeAngle(toAngle);
    let delta = na2 - na1;
    if (useCcw) {
        while (delta <= 1e-9) delta += 2 * Math.PI;
    } else {
        while (delta >= -1e-9) delta -= 2 * Math.PI;
    }
    const pts = [];
    for (let i = 0; i <= steps; i++) {
        const t = na1 + (delta * i) / steps;
        pts.push({
            x: marker.x + r * Math.cos(t),
            y: marker.y + r * Math.sin(t),
        });
    }
    return pts;
};

/** Signed area on the bulge side of chord (start→end); 0 on the chord line. */
const bulgeSideOfChord = (geom, x, y) => {
    const midX = (geom.x1 + geom.x2) / 2;
    const midY = (geom.y1 + geom.y2) / 2;
    return (
        (x - midX) * (geom.y2 - geom.y1) -
        (y - midY) * (geom.x2 - geom.x1)
    );
};

const dedupePolygonPoints = (pts, tol = 1.5) => {
    const out = [];
    pts.forEach((p) => {
        if (!out.length || Math.hypot(p.x - out[out.length - 1].x, p.y - out[out.length - 1].y) > tol) {
            out.push(p);
        }
    });
    return out;
};

/** Point on the bin disk toward the column interior (away from an exterior arc bulge). */
const binInteriorProbe = (marker, geom) => {
    const r = getMarkerRadiusImagePx(marker) * 0.48;
    const vx = (geom?.curveX ?? marker.x) - marker.x;
    const vy = (geom?.curveY ?? marker.y) - marker.y;
    const len = Math.hypot(vx, vy) || 1;
    return {
        x: marker.x - (vx / len) * r,
        y: marker.y - (vy / len) * r,
    };
};

/** True when polygon covers too much of a bin disk (not just grazing the perimeter). */
const polygonIntrudesBinDisk = (poly, marker, maxInnerSamples = 1) => {
    const r = getMarkerRadiusImagePx(marker);
    if (r < 4 || !poly?.length) return false;
    let hits = 0;
    for (let k = 0; k < 8; k++) {
        const a = k * (Math.PI / 4);
        const px = marker.x + r * 0.62 * Math.cos(a);
        const py = marker.y + r * 0.62 * Math.sin(a);
        if (pointInPolygon(px, py, poly)) hits += 1;
    }
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        for (const t of [0.25, 0.5, 0.75]) {
            const ex = poly[i].x + t * (poly[j].x - poly[i].x);
            const ey = poly[i].y + t * (poly[j].y - poly[i].y);
            if (
                Math.hypot(ex - marker.x, ey - marker.y) < r * 0.68 &&
                pointInPolygon(ex, ey, poly)
            ) {
                hits += 1;
            }
        }
    }
    return hits > maxInnerSamples;
};

/** Min area (px²) for a void between round bins — scales down with small bin circles on canvas. */
const minBinGapPocketAreaPx = (markers, involvedIds = []) => {
    const list = (markers || []).filter((m) =>
        involvedIds.length ? involvedIds.includes(m.id) : true
    );
    const radii = list
        .map((m) => getMarkerRadiusImagePx(m))
        .filter((r) => r >= 4);
    const r = radii.length ? Math.min(...radii) : 24;
    return Math.max(12, (r * 0.32) ** 2);
};

const polygonValidForArcPocket = (poly, markers, involvedMarkerIds = []) => {
    if (!poly || poly.length < 3) return false;
    const involved = involvedMarkerIds || [];
    const minArea = involved.length
        ? minBinGapPocketAreaPx(markers, involved)
        : 80;
    if (Math.abs(polygonArea(poly)) < minArea) return false;
    const xs = poly.map((p) => p.x);
    const ys = poly.map((p) => p.y);
    const pad = 8;
    const bb = {
        minX: Math.min(...xs) - pad,
        maxX: Math.max(...xs) + pad,
        minY: Math.min(...ys) - pad,
        maxY: Math.max(...ys) + pad,
    };
    for (const m of markers) {
        if (m.x < bb.minX || m.x > bb.maxX || m.y < bb.minY || m.y > bb.maxY) continue;
        if (involved.includes(m.id)) continue;
        if (pointInPolygon(m.x, m.y, poly)) return false;
        if (polygonIntrudesBinDisk(poly, m, 1)) return false;
    }
    return true;
};

/** 2×2 bin-hub void — only guard involved bin centers; neighbor columns must not block the pocket. */
const polygonValidFor2x2BinHubPocket = (poly, markers, involvedMarkerIds) => {
    if (!poly || poly.length < 3) return false;
    const involved = involvedMarkerIds || [];
    const minArea = minBinGapPocketAreaPx(markers, involved);
    if (Math.abs(polygonArea(poly)) < minArea) return false;
    for (const id of involved) {
        const m = markers.find((mk) => mk.id === id);
        if (!m) continue;
        if (pointInPolygon(m.x, m.y, poly)) return false;
    }
    const c = polygonCentroid(poly);
    return pointInPolygon(c.x, c.y, poly);
};

const arcPocketIntrudesInvolvedBinInterior = (poly, geom, markers, involved) => {
    for (const id of involved) {
        const m = markers.find((mk) => mk.id === id);
        if (!m) continue;
        const probe = binInteriorProbe(m, geom);
        if (pointInPolygon(probe.x, probe.y, poly)) return true;
    }
    return false;
};

/** Map segment id → robust interior probe point for each user arc. */
const arcInteriorProbesBySegmentId = (resolvedById) => {
    const map = new Map();
    resolvedById.forEach((geom, segId) => {
        if (geom.kind === "arc" && geom.curveX != null && geom.curveY != null) {
            map.set(segId, arcInteriorTestPoint(geom));
        }
    });
    return map;
};

/**
 * A face that uses a user arc must contain that arc's interior probe (a point just
 * inside the pocket, toward the chord). Graph walks often pick the wrong interior wedge
 * inside each bin or an arc+chord wedge that does not match the user's drawn curve.
 */
const faceContainsRequiredArcBulge = (face, arcProbes) => {
    for (const sid of face.segmentIds || []) {
        const probe = arcProbes.get(sid);
        if (probe && !pointInPolygon(probe.x, probe.y, face.polygon)) return false;
    }
    return true;
};

/** Slightly-offset point INSIDE an arc pocket (toward the chord), used for robust hit-testing. */
const arcInteriorTestPoint = (geom) => {
    const mid = { x: (geom.x1 + geom.x2) / 2, y: (geom.y1 + geom.y2) / 2 };
    const dx = mid.x - geom.curveX;
    const dy = mid.y - geom.curveY;
    const len = Math.hypot(dx, dy);
    if (len < 1e-3) return { x: geom.curveX, y: geom.curveY };
    const step = Math.min(8, Math.max(2, len * 0.1));
    return {
        x: geom.curveX + (dx / len) * step,
        y: geom.curveY + (dy / len) * step,
    };
};

/**
 * Explicit arc-bounded pocket: user arc + bin perimeter legs on the bulge side, joined
 * at the bin-to-bin contact (with a straight chord if the bins do not touch).
 * Planar graph often yields arc+chord (through bin interiors) instead of this shape.
 *
 * Two-bin arcs: legs run on the side nearest the bulge. One-bin / unresolved cases:
 * just close the arc with a chord between its endpoints.
 */
const findWallSegmentBetweenMarkers = (m1, m2, segments, markers) => {
    const all = findAllWallSegmentsBetweenMarkers(m1, m2, segments, markers);
    return all.length ? all[0] : null;
};

/** Every straight wall snapped between the same two bins (order = segment list). */
const findAllWallSegmentsBetweenMarkers = (m1, m2, segments, markers) => {
    const hits = [];
    for (const raw of segments || []) {
        const g = resolveSegmentGeometry(raw, markers, segments);
        if (!g || g.kind !== "wall") continue;
        const ids = [g.markerId1, g.markerId2].filter(Boolean);
        if (ids.includes(m1.id) && ids.includes(m2.id)) {
            hits.push({ segId: raw.id, geom: g });
        }
    }
    return hits;
};

/**
 * When several walls span the same bin pair, the arc pocket must use the wall
 * nearest the arc (e.g. middle wall), not the first wall in the list (often the
 * top wall), or the synthesized pocket swallows A+H into one region.
 */
const findBestWallForArcPocket = (arcGeom, m1, m2, segments, markers) => {
    const walls = findAllWallSegmentsBetweenMarkers(m1, m2, segments, markers);
    if (!walls.length) return null;
    if (walls.length === 1) return walls[0].geom;

    const arcRef = {
        x:
            arcGeom.curveX ??
            (arcGeom.x1 + arcGeom.x2) / 2,
        y:
            arcGeom.curveY ??
            (arcGeom.y1 + arcGeom.y2) / 2,
    };

    let best = walls[0];
    let bestDist = Infinity;
    walls.forEach((w) => {
        const wmx = (w.geom.x1 + w.geom.x2) / 2;
        const wmy = (w.geom.y1 + w.geom.y2) / 2;
        const d = Math.hypot(wmx - arcRef.x, wmy - arcRef.y);
        if (d < bestDist) {
            bestDist = d;
            best = w;
        }
    });
    return best.geom;
};

const findWallBetweenMarkers = (m1, m2, segments, markers, arcGeom = null) => {
    if (arcGeom) {
        return findBestWallForArcPocket(arcGeom, m1, m2, segments, markers);
    }
    const hit = findWallSegmentBetweenMarkers(m1, m2, segments, markers);
    return hit ? hit.geom : null;
};

const graphFaceUserWallCount = (face, userSegmentIds, resolvedById) =>
    (face.segmentIds || []).filter((id) => {
        if (!userSegmentIds.has(id)) return false;
        return resolvedById.get(id)?.kind === "wall";
    }).length;

const wallEndpointsOnMarkers = (wallGeom, m1, m2) => {
    if (wallGeom.markerId1 === m1.id) {
        return {
            onM1: { x: wallGeom.x1, y: wallGeom.y1, angle: wallGeom.angle1 },
            onM2: { x: wallGeom.x2, y: wallGeom.y2, angle: wallGeom.angle2 },
        };
    }
    return {
        onM1: { x: wallGeom.x2, y: wallGeom.y2, angle: wallGeom.angle2 },
        onM2: { x: wallGeom.x1, y: wallGeom.y1, angle: wallGeom.angle1 },
    };
};

/** Perimeter leg between arc and wall that stays on the bin exterior (not through the interior). */
const pickBinLegArcToWall = (marker, fromAngle, toAngle, geom) =>
    pickBinLegAlongBulgeSide(marker, fromAngle, toAngle, geom);

const assembleWallArcPocket = (arcPts, leg2, leg1, onM2, onM1) =>
    dedupePolygonPoints([
        ...arcPts,
        ...leg2.slice(1),
        { x: onM2.x, y: onM2.y },
        { x: onM1.x, y: onM1.y },
        ...leg1.slice(1),
    ]);

const assembleTwoBinArcPocket = (arcPts, leg2, leg1) =>
    dedupePolygonPoints([...arcPts, ...leg2.slice(1), ...leg1.slice(1)]);

const tryBestArcPocketCandidate = (
    variants,
    accept,
    m1,
    m2,
    geom,
    requireBulgeLegs = true
) => {
    let best = null;
    let bestArea = 0;
    for (const build of variants) {
        const poly = build();
        if (!poly?.length || !accept(poly)) continue;
        if (requireBulgeLegs && !arcPocketCoversExteriorBinLegs(poly, m1, m2, geom))
            continue;
        const area = Math.abs(polygonArea(poly));
        if (area > bestArea) {
            bestArea = area;
            best = poly;
        }
    }
    return best;
};

/** Pocket bounded by user arc + wall + bin perimeters (no straight wall chord across the pocket). */
const buildWallBoundedArcPocket = (geom, m1, m2, wallGeom, markers) => {
    const involved = [m1.id, m2.id];
    const { onM1, onM2 } = wallEndpointsOnMarkers(wallGeom, m1, m2);
    const arcPts = sampleUserArc(geom, 48);
    const aW1 = onM1.angle ?? wallAngleOnMarker(wallGeom, m1, markers);
    const aW2 = onM2.angle ?? wallAngleOnMarker(wallGeom, m2, markers);
    const c1 = partnerContactAngle(m1, m2);
    const c2 = partnerContactAngle(m2, m1);

    const accept = (poly) =>
        wallArcPocketAcceptsPolygon(poly, geom, markers, involved, wallGeom);

    return tryBestArcPocketCandidate(
        [
            // Route each bin leg along the side facing the PARTNER bin (the gap side).
            // Distance-to-bulge scoring wraps the long way around a bin when the wall
            // contact sits roughly opposite the arc contact (stacked bins with a gap),
            // which swallows the bin's center. Partner-facing legs stay in the gap.
            () => {
                const leg2 = pickPerimeterLegTowardPartner(
                    m2,
                    geom.angle2,
                    aW2,
                    m1
                );
                const leg1 = pickPerimeterLegTowardPartner(
                    m1,
                    aW1,
                    geom.angle1,
                    m2
                );
                return assembleWallArcPocket(arcPts, leg2, leg1, onM2, onM1);
            },
            () =>
                dedupePolygonPoints([
                    ...arcPts,
                    ...sampleExteriorBinLegTowardBulge(m2, geom.angle2, c2, geom).slice(
                        1
                    ),
                    ...sampleExteriorBinLegTowardBulge(m2, c2, aW2, geom).slice(1),
                    ...sampleExteriorBinLegTowardBulge(m1, aW1, c1, geom).slice(1),
                    ...sampleExteriorBinLegTowardBulge(m1, c1, geom.angle1, geom).slice(
                        1
                    ),
                ]),
            () =>
                dedupePolygonPoints([
                    ...arcPts,
                    ...sampleExteriorBinLegTowardBulge(m2, geom.angle2, aW2, geom).slice(
                        1
                    ),
                    ...sampleExteriorBinLegTowardBulge(m1, aW1, geom.angle1, geom).slice(1),
                ]),
            () => {
                const leg2 = sampleExteriorBinLegTowardBulge(
                    m2,
                    geom.angle2,
                    aW2,
                    geom
                );
                const leg1 = sampleExteriorBinLegTowardBulge(
                    m1,
                    aW1,
                    geom.angle1,
                    geom
                );
                return assembleWallArcPocket(arcPts, leg2, leg1, onM2, onM1);
            },
        ],
        accept,
        m1,
        m2,
        geom,
        false
    );
};

const buildOpenTwoBinArcPocket = (geom, m1, m2, markers) => {
    const involved = [m1.id, m2.id];
    const arcPts = sampleUserArc(geom, 48);
    const pArc2 = pointOnBinPerimeter(m2, geom.angle2);
    const pArc1 = pointOnBinPerimeter(m1, geom.angle1);
    const toward2 = Math.atan2(pArc2.y - m1.y, pArc2.x - m1.x);
    const toward1 = Math.atan2(pArc1.y - m2.y, pArc1.x - m2.x);
    const c1 = partnerContactAngle(m1, m2);
    const c2 = partnerContactAngle(m2, m1);

    const accept = (poly) =>
        arcPocketAcceptsPolygon(poly, geom, markers, involved);

    const best = tryBestArcPocketCandidate(
        [
            () =>
                assembleTwoBinArcPocket(
                    arcPts,
                    sampleExteriorBinLegTowardBulge(m2, geom.angle2, c2, geom),
                    sampleExteriorBinLegTowardBulge(m1, c1, geom.angle1, geom)
                ),
            () =>
                assembleTwoBinArcPocket(
                    arcPts,
                    sampleExteriorBinLegTowardBulge(m2, c2, geom.angle2, geom),
                    sampleExteriorBinLegTowardBulge(m1, geom.angle1, c1, geom)
                ),
            () =>
                assembleTwoBinArcPocket(
                    arcPts,
                    sampleExteriorBinLegTowardBulge(m2, geom.angle2, toward1, geom),
                    sampleExteriorBinLegTowardBulge(m1, toward2, geom.angle1, geom)
                ),
            () =>
                assembleTwoBinArcPocket(
                    arcPts,
                    sampleExteriorBinLeg(m2, geom.angle2, c2, geom),
                    sampleExteriorBinLeg(m1, c1, geom.angle1, geom)
                ),
        ],
        accept,
        m1,
        m2,
        geom
    );
    if (best) return best;

    const relaxed = assembleTwoBinArcPocket(
        arcPts,
        sampleExteriorBinLegTowardBulge(m2, geom.angle2, c2, geom),
        sampleExteriorBinLegTowardBulge(m1, c1, geom.angle1, geom)
    );
    return accept(relaxed) ? relaxed : null;
};

const buildTwoBinArcPocketPolygon = (geom, m1, m2, markers, segments) => {
    const wall = findWallBetweenMarkers(m1, m2, segments, markers, geom);
    const open = buildOpenTwoBinArcPocket(geom, m1, m2, markers);
    if (!wall) return open;

    const walled = buildWallBoundedArcPocket(geom, m1, m2, wall, markers);
    if (!open) return walled;
    if (!walled) return open;
    const openArea = Math.abs(polygonArea(open));
    const walledArea = Math.abs(polygonArea(walled));
    return walledArea >= openArea ? walled : open;
};

const synthesizeArcPocketFaces = (markers, resolvedById, segments = []) => {
    const faces = [];
    resolvedById.forEach((geom, segId) => {
        if (geom.kind !== "arc" || geom.curveX == null) return;

        const m1 =
            geom.markerId1 != null ? markers.find((m) => m.id === geom.markerId1) : null;
        const m2 =
            geom.markerId2 != null ? markers.find((m) => m.id === geom.markerId2) : null;

        const arcPts = sampleUserArc(geom, 48);

        let poly = null;
        const involved = [];

        if (m1 && m2 && m1.id !== m2.id && geom.angle1 != null && geom.angle2 != null) {
            poly = buildTwoBinArcPocketPolygon(geom, m1, m2, markers, segments);
        } else if (m1 && geom.angle1 != null && geom.angle2 != null && m2 == null) {
            involved.push(m1.id);
            const leg = sampleExteriorBinLegTowardBulge(
                m1,
                geom.angle2,
                geom.angle1,
                geom
            );
            const candidate = dedupePolygonPoints([...arcPts, ...leg.slice(1)]);
            if (arcPocketAcceptsPolygon(candidate, geom, markers, involved)) {
                poly = candidate;
            }
        } else {
            if (m1) involved.push(m1.id);
            if (m2) involved.push(m2.id);
            if (m1 && m2 && geom.angle1 != null && geom.angle2 != null) {
                poly = buildOpenTwoBinArcPocket(geom, m1, m2, markers);
            }
        }

        if (!poly) return;

        faces.push({
            id: `arc:${segId}`,
            polygon: poly,
            centroid: polygonCentroid(poly),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [segId],
            binArcKeys: [],
        });
    });
    return faces;
};

/**
 * Wedge closed off by a single straight wall whose two ends snap to two touching bins.
 * Bounded by the wall plus each bin's perimeter down to the bin-to-bin contact point.
 * The planar graph builds this in the normal path; the synthesis-only fast path used for
 * large layouts (≥ LAYOUT_HEAVY_OBJECT_LIMIT objects) needs it added explicitly.
 */
const synthesizeWallBinWedgePockets = (markers, resolvedById, segments) => {
    const faces = [];
    resolvedById.forEach((geom, segId) => {
        if (geom.kind !== "wall") return;
        const m1 =
            geom.markerId1 != null ? markers.find((m) => m.id === geom.markerId1) : null;
        const m2 =
            geom.markerId2 != null ? markers.find((m) => m.id === geom.markerId2) : null;
        if (!m1 || !m2 || m1.id === m2.id) return;
        if (!binsAreSnappedTogether(m1, m2)) return;

        const { onM1, onM2 } = wallEndpointsOnMarkers(geom, m1, m2);
        const a1 = onM1.angle ?? wallAngleOnMarker(geom, m1, markers);
        const a2 = onM2.angle ?? wallAngleOnMarker(geom, m2, markers);
        const c1 = partnerContactAngle(m1, m2);
        const c2 = partnerContactAngle(m2, m1);
        const wmx = (geom.x1 + geom.x2) / 2;
        const wmy = (geom.y1 + geom.y2) / 2;

        const legM2 = chooseBinLegToward(m2, a2, c2, wmx, wmy);
        const legM1 = chooseBinLegToward(m1, c1, a1, wmx, wmy);
        const poly = dedupePolygonPoints([
            { x: onM1.x, y: onM1.y },
            ...legM2,
            ...legM1.slice(1),
        ]);

        const involved = [m1.id, m2.id];
        if (!polygonValidForArcPocket(poly, markers, involved)) return;
        const cent = polygonCentroid(poly);
        if (!pointInPolygon(cent.x, cent.y, poly)) return;

        faces.push({
            id: `wallwedge:${segId}:${m1.id}:${m2.id}`,
            polygon: poly,
            centroid: cent,
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [segId],
            binArcKeys: [],
        });
    });
    return faces;
};

const wallAngleOnMarker = (geom, marker, markers) => {
    if (geom.markerId1 === marker.id && geom.angle1 != null) return geom.angle1;
    if (geom.markerId2 === marker.id && geom.angle2 != null) return geom.angle2;
    const x = geom.markerId1 === marker.id ? geom.x1 : geom.x2;
    const y = geom.markerId1 === marker.id ? geom.y1 : geom.y2;
    const inf = inferBinSnap(x, y, markers);
    if (inf?.markerId === marker.id) return inf.angle;
    return Math.atan2(y - marker.y, x - marker.x);
};

const polygonValidForChannelPocket = (poly, markers) => {
    if (!poly || poly.length < 3) return false;
    const area = Math.abs(polygonArea(poly));
    if (area < 200) return false;
    if (markers?.some((m) => pointInPolygon(m.x, m.y, poly))) return false;
    if (markers?.some((m) => polygonIntrudesBinDisk(poly, m, 1))) return false;
    const c = polygonCentroid(poly);
    return pointInPolygon(c.x, c.y, poly);
};

/**
 * Center pocket in a 2×2 bin block with parallel vertical (or horizontal) channel walls.
 * Planar graph often misses this loop when top/bottom are arc pockets C/T.
 */
const buildWallChannelPocketPolygon = (tl, tr, bl, br, leftGeom, rightGeom, markers) => {
    const angTL = wallAngleOnMarker(leftGeom, tl, markers);
    const angBL = wallAngleOnMarker(leftGeom, bl, markers);
    const angTR = wallAngleOnMarker(rightGeom, tr, markers);
    const angBR = wallAngleOnMarker(rightGeom, br, markers);

    const pTL = pointOnBinPerimeter(tl, angTL);
    const pBL = pointOnBinPerimeter(bl, angBL);
    const pTR = pointOnBinPerimeter(tr, angTR);
    const pBR = pointOnBinPerimeter(br, angBR);

    const topOnLeft = pTL.y <= pBL.y;
    const topTL = topOnLeft ? angTL : angBL;
    const botBL = topOnLeft ? angBL : angTL;
    const topTR = pTR.y <= pBR.y ? angTR : angBR;
    const botBR = pTR.y <= pBR.y ? angBR : angTR;

    const towardTR = Math.atan2(tr.y - tl.y, tr.x - tl.x);
    const towardTL = Math.atan2(tl.y - tr.y, tl.x - tr.x);
    const towardBR = Math.atan2(br.y - bl.y, br.x - bl.x);
    const towardBL = Math.atan2(bl.y - br.y, bl.x - br.x);

    const topLegTL = pickPerimeterLegTowardPartner(tl, topTL, towardTR, tr);
    const topLegTR = pickPerimeterLegTowardPartner(tr, towardTL, topTR, tl);
    const botLegBL = pickPerimeterLegTowardPartner(bl, botBL, towardBR, br);
    const botLegBR = pickPerimeterLegTowardPartner(br, towardBL, botBR, bl);

    const leftWall = sampleWallPolyline(leftGeom, tl, bl, markers);
    const rightWall = sampleWallPolyline(rightGeom, tr, br, markers).slice().reverse();

    return dedupePolygonPoints([
        ...leftWall,
        ...botLegBL.slice(1),
        ...botLegBR.slice(1),
        ...rightWall.slice(1),
        ...topLegTR.slice(1),
        ...topLegTL.slice(1),
    ]);
};

const sampleWallPolyline = (geom, topMarker, botMarker, markers) => {
    const aTop = wallAngleOnMarker(geom, topMarker, markers);
    const aBot = wallAngleOnMarker(geom, botMarker, markers);
    const pTop = pointOnBinPerimeter(topMarker, aTop);
    const pBot = pointOnBinPerimeter(botMarker, aBot);
    if (pTop.y <= pBot.y) return [pTop, pBot];
    return [pBot, pTop];
};

/** Nearest round-bin marker to a point (for walls drawn on perimeter without snap ids). */
const nearestMarkerToPoint = (x, y, markers, maxDistPx) => {
    let best = null;
    let bestD = maxDistPx;
    (markers || []).forEach((m) => {
        const d = Math.hypot(x - m.x, y - m.y);
        const limit = getMarkerRadiusImagePx(m) + maxDistPx;
        if (d <= limit && d < bestD) {
            bestD = d;
            best = m;
        }
    });
    return best;
};

const resolveWallBinAnchors = (geom, markers) => {
    let m1 = geom.markerId1 != null ? markers.find((m) => m.id === geom.markerId1) : null;
    let m2 = geom.markerId2 != null ? markers.find((m) => m.id === geom.markerId2) : null;
    const inf1 = inferBinSnap(geom.x1, geom.y1, markers);
    const inf2 = inferBinSnap(geom.x2, geom.y2, markers);
    if (!m1 && inf1) m1 = markers.find((m) => m.id === inf1.markerId);
    if (!m2 && inf2) m2 = markers.find((m) => m.id === inf2.markerId);
    if (!m1) m1 = nearestMarkerToPoint(geom.x1, geom.y1, markers, 14);
    if (!m2) m2 = nearestMarkerToPoint(geom.x2, geom.y2, markers, 14);
    return [m1, m2];
};

const markersForWallGeom = (geom, markers) => resolveWallBinAnchors(geom, markers);

/** Order four markers as tl, tr, bl, br around a snapped 2×2 hub (any rotation). */
const orderMarkersAs2x2 = (m1, m2, m3, m4) => {
    const bins = [m1, m2, m3, m4];
    if (new Set(bins.map((b) => b.id)).size < 4) return null;
    const hub = {
        x: bins.reduce((s, m) => s + m.x, 0) / 4,
        y: bins.reduce((s, m) => s + m.y, 0) / 4,
    };
    const byAngle = [...bins].sort(
        (a, b) =>
            Math.atan2(a.y - hub.y, a.x - hub.x) -
            Math.atan2(b.y - hub.y, b.x - hub.x)
    );
    for (let rot = 0; rot < 4; rot++) {
        const tl = byAngle[rot % 4];
        const tr = byAngle[(rot + 1) % 4];
        const br = byAngle[(rot + 2) % 4];
        const bl = byAngle[(rot + 3) % 4];
        if (
            !binsAreSnappedTogether(tl, tr) ||
            !binsAreSnappedTogether(tr, br) ||
            !binsAreSnappedTogether(br, bl) ||
            !binsAreSnappedTogether(bl, tl) ||
            binsAreSnappedTogether(tl, br) ||
            binsAreSnappedTogether(tr, bl)
        ) {
            continue;
        }
        const poly = build2x2BinCentralPocketPolygon(tl, tr, bl, br);
        if (poly?.length && pointInPolygon(hub.x, hub.y, poly)) {
            return { tl, tr, bl, br };
        }
    }
    return null;
};

const gridFromChannelWallPair = (wL, wR) => {
    const ids = new Set([wL.m1.id, wL.m2.id, wR.m1.id, wR.m2.id]);
    if (ids.size < 4) return null;
    return orderMarkersAs2x2(wL.m1, wL.m2, wR.m1, wR.m2);
};

/** Detect parallel channel walls between two columns of a 2×2 snapped bin grid. */
const findWallChannelGrid = (markers, resolvedById) => {
    const walls = [];
    resolvedById.forEach((geom, segId) => {
        if (geom.kind !== "wall") return;
        const [m1, m2] = markersForWallGeom(geom, markers);
        if (!m1 || !m2 || m1.id === m2.id) return;
        walls.push({ segId, geom, m1, m2 });
    });

    for (let i = 0; i < walls.length; i++) {
        const wL = walls[i];
        const dxL = wL.m2.x - wL.m1.x;
        const dyL = wL.m2.y - wL.m1.y;
        const vertical = Math.abs(dyL) >= Math.abs(dxL) * 1.2;
        const topL = wL.m1.y <= wL.m2.y ? wL.m1 : wL.m2;
        const botL = wL.m1.y <= wL.m2.y ? wL.m2 : wL.m1;

        for (let j = i + 1; j < walls.length; j++) {
            const wR = walls[j];
            const dxR = wR.m2.x - wR.m1.x;
            const dyR = wR.m2.y - wR.m1.y;
            const verticalR = Math.abs(dyR) >= Math.abs(dxR) * 1.2;
            if (vertical !== verticalR) continue;

            const topR = wR.m1.y <= wR.m2.y ? wR.m1 : wR.m2;
            const botR = wR.m1.y <= wR.m2.y ? wR.m2 : wR.m1;

            if (vertical) {
                if (topR.x <= topL.x + 4) continue;
                if (
                    Math.abs(topR.x - botR.x) > 8 ||
                    Math.abs(topL.x - botL.x) > 8
                ) {
                    continue;
                }
            } else {
                if (topR.y <= topL.y + 4) continue;
                if (
                    Math.abs(topR.y - botR.y) > 8 ||
                    Math.abs(topL.y - botL.y) > 8
                ) {
                    continue;
                }
            }

            const grid = gridFromChannelWallPair(wL, wR);
            if (!grid) continue;

            return {
                ...grid,
                leftSegId: wL.segId,
                rightSegId: wR.segId,
                leftGeom: wL.geom,
                rightGeom: wR.geom,
            };
        }
    }
    return null;
};

const wallIsMostlyHorizontal = (geom) => {
    const dx = Math.abs(geom.x2 - geom.x1);
    const dy = Math.abs(geom.y2 - geom.y1);
    return dx >= dy * 1.8 && dx > 6;
};

/** Channel pocket only: optional top/bottom horizontals between the two channel walls. */
const tryChannelRectFromHorizontalWalls = (grid, markers, resolvedById) => {
    const { leftGeom, rightGeom, leftSegId, rightSegId } = grid;
    const xL = Math.min(
        (leftGeom.x1 + leftGeom.x2) / 2,
        (rightGeom.x1 + rightGeom.x2) / 2
    );
    const xR = Math.max(
        (leftGeom.x1 + leftGeom.x2) / 2,
        (rightGeom.x1 + rightGeom.x2) / 2
    );
    const vYmin = Math.min(
        leftGeom.y1,
        leftGeom.y2,
        rightGeom.y1,
        rightGeom.y2
    );
    const vYmax = Math.max(
        leftGeom.y1,
        leftGeom.y2,
        rightGeom.y1,
        rightGeom.y2
    );

    const horiz = [];
    resolvedById.forEach((geom, segId) => {
        if (geom.kind !== "wall" || !wallIsMostlyHorizontal(geom)) return;
        if (segId === leftSegId || segId === rightSegId) return;
        const y = (geom.y1 + geom.y2) / 2;
        const minX = Math.min(geom.x1, geom.x2);
        const maxX = Math.max(geom.x1, geom.x2);
        const spanTol = Math.max(
            8,
            (getMarkerRadiusImagePx(grid.tl) +
                getMarkerRadiusImagePx(grid.tr)) *
                0.35
        );
        if (minX > xL + spanTol || maxX < xR - spanTol) return;
        if (y < vYmin - 20 || y > vYmax + 20) return;
        horiz.push({ segId, y });
    });
    if (horiz.length < 2) return null;

    horiz.sort((a, b) => a.y - b.y);
    const yTop = horiz[0].y;
    const yBot = horiz[horiz.length - 1].y;
    if (yBot - yTop < 12) return null;

    const pad = 1.5;
    const poly = [
        { x: xL + pad, y: yTop + pad },
        { x: xR - pad, y: yTop + pad },
        { x: xR - pad, y: yBot - pad },
        { x: xL + pad, y: yBot - pad },
    ];
    if (!polygonValidForChannelPocket(poly, markers)) return null;

    const channelWidth = xR - xL;
    const channelHeight = yBot - yTop;
    const maxW =
        getMarkerRadiusImagePx(grid.tl) +
        getMarkerRadiusImagePx(grid.tr) +
        getMarkerRadiusImagePx(grid.bl) +
        getMarkerRadiusImagePx(grid.br);
    if (channelWidth > maxW * 1.35 || channelHeight > maxW * 1.35) return null;

    return {
        id: `channel:${leftSegId}:${rightSegId}`,
        polygon: poly,
        centroid: polygonCentroid(poly),
        labelPoint: polygonLabelPoint(poly, markers),
        area: Math.abs(polygonArea(poly)),
        segmentIds: [
            leftSegId,
            rightSegId,
            ...horiz.map((h) => h.segId),
        ],
        binArcKeys: [],
    };
};

const wallEndpointsMeet = (g1, g2, tol = JUNCTION_TOL_PX + 2) => {
    const pts1 = [
        { x: g1.x1, y: g1.y1 },
        { x: g1.x2, y: g1.y2 },
    ];
    const pts2 = [
        { x: g2.x1, y: g2.y1 },
        { x: g2.x2, y: g2.y2 },
    ];
    return pts1.some((p1) =>
        pts2.some((p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y) <= tol)
    );
};

/** Four walls that meet at corners (user-drawn box in a bin channel). Capped to avoid site-wide spam. */
const synthesizeJunctionClosedWallRects = (markers, resolvedById) => {
    const vert = [];
    const horiz = [];
    resolvedById.forEach((geom, segId) => {
        if (geom.kind !== "wall") return;
        if (wallIsMostlyHorizontal(geom)) horiz.push({ segId, geom });
        else if (Math.abs(geom.y2 - geom.y1) >= Math.abs(geom.x2 - geom.x1) * 1.2) {
            vert.push({ segId, geom });
        }
    });
    if (vert.length < 2 || horiz.length < 2) return [];
    if (vert.length * horiz.length > 2500) return [];

    const faces = [];
    const seen = new Set();
    const maxFaces = 32;

    for (let i = 0; i < vert.length && faces.length < maxFaces; i++) {
        for (let j = i + 1; j < vert.length && faces.length < maxFaces; j++) {
            const vL = vert[i];
            const vR = vert[j];
            const xLi = (vL.geom.x1 + vL.geom.x2) / 2;
            const xRj = (vR.geom.x1 + vR.geom.x2) / 2;
            if (Math.abs(xLi - xRj) < 12) continue;
            const xL = Math.min(xLi, xRj);
            const xR = Math.max(xLi, xRj);

            for (let a = 0; a < horiz.length && faces.length < maxFaces; a++) {
                for (let b = a + 1; b < horiz.length && faces.length < maxFaces; b++) {
                    const hT = horiz[a];
                    const hB = horiz[b];
                    const yAi = (hT.geom.y1 + hT.geom.y2) / 2;
                    const yBj = (hB.geom.y1 + hB.geom.y2) / 2;
                    if (Math.abs(yAi - yBj) < 12) continue;
                    const meetTol = JUNCTION_TOL_PX + 4;
                    const cornersMeet =
                        wallEndpointsMeet(vL.geom, hT.geom, meetTol) &&
                        wallEndpointsMeet(vL.geom, hB.geom, meetTol) &&
                        wallEndpointsMeet(vR.geom, hT.geom, meetTol) &&
                        wallEndpointsMeet(vR.geom, hB.geom, meetTol);
                    if (!cornersMeet) continue;
                    const yTop = Math.min(yAi, yBj);
                    const yBot = Math.max(yAi, yBj);
                    const pad = 1.5;
                    const poly = [
                        { x: xL + pad, y: yTop + pad },
                        { x: xR - pad, y: yTop + pad },
                        { x: xR - pad, y: yBot - pad },
                        { x: xL + pad, y: yBot - pad },
                    ];
                    const segKey = [vL.segId, vR.segId, hT.segId, hB.segId]
                        .sort()
                        .join(",");
                    const id = `wallloop:${segKey}`;
                    if (seen.has(id)) continue;
                    if (!polygonValidForChannelPocket(poly, markers)) continue;
                    const w = xR - xL;
                    const h = yBot - yTop;
                    if (w > 600 || h > 600 || w < 10 || h < 10) continue;
                    seen.add(id);
                    faces.push({
                        id,
                        polygon: poly,
                        centroid: polygonCentroid(poly),
                        labelPoint: polygonLabelPoint(poly, markers),
                        area: Math.abs(polygonArea(poly)),
                        segmentIds: [vL.segId, vR.segId, hT.segId, hB.segId],
                        binArcKeys: [],
                    });
                }
            }
        }
    }
    return faces;
};

/** Strip pocket on the interior side of a wall between two snapped bins (toward 2×2 hub). */
const buildWallStripTowardHub = (geom, m1, m2, hub, markers) => {
    const { onM1, onM2 } = wallEndpointsOnMarkers(geom, m1, m2);
    const a1 = onM1.angle ?? wallAngleOnMarker(geom, m1, markers);
    const a2 = onM2.angle ?? wallAngleOnMarker(geom, m2, markers);
    const h1 = Math.atan2(hub.y - m1.y, hub.x - m1.x);
    const h2 = Math.atan2(hub.y - m2.y, hub.x - m2.x);
    const leg1 = chooseBinLegToward(m1, a1, h1, hub.x, hub.y);
    const leg2 = chooseBinLegToward(m2, a2, h2, hub.x, hub.y);
    return dedupePolygonPoints([
        { x: onM1.x, y: onM1.y },
        ...leg1.slice(1),
        ...leg2.slice(1),
        { x: onM2.x, y: onM2.y },
    ]);
};

/** User walls whose midpoints lie near the 2×2 hub (center cross dividers). */
const findInteriorCrossWallsNearHub = (hub, segments, markers, radiusPx) => {
    const vert = [];
    const horiz = [];
    (segments || []).forEach((raw) => {
        const g = resolveSegmentGeometry(raw, markers, segments);
        if (!g || g.kind !== "wall") return;
        const mx = (g.x1 + g.x2) / 2;
        const my = (g.y1 + g.y2) / 2;
        if (Math.hypot(mx - hub.x, my - hub.y) > radiusPx) return;
        const entry = { geom: g, segId: raw.id };
        if (wallIsMostlyHorizontal(g)) horiz.push(entry);
        else if (Math.abs(g.y2 - g.y1) >= Math.abs(g.x2 - g.x1) * 1.2) {
            vert.push(entry);
        }
    });
    return { vert, horiz };
};

/**
 * One interior cell of a 2×2 cross (hub corner + two channel walls + bin perimeter).
 * Rectangles through the hub fail validation because they cover bin centers.
 */
const build2x2QuadrantHubPocket = (
    corner,
    rowMate,
    colMate,
    rowWall,
    colWall,
    split,
    markers
) => {
    if (!rowWall || !colWall) return null;
    const hubPt = { x: split.xMid, y: split.yMid };
    const rowGeom = rowWall.geom;
    const colGeom = colWall.geom;
    const rowEnds = wallEndpointsOnMarkers(rowGeom, corner, rowMate);
    const colEnds = wallEndpointsOnMarkers(colGeom, corner, colMate);
    const rowOn =
        rowGeom.markerId1 === corner.id ? rowEnds.onM1 : rowEnds.onM2;
    const colOn =
        colGeom.markerId1 === corner.id ? colEnds.onM1 : colEnds.onM2;
    const rowAng = wallAngleOnMarker(rowGeom, corner, markers);
    const colAng = wallAngleOnMarker(colGeom, corner, markers);
    const periLeg = pickPerimeterLegTowardPartner(
        corner,
        rowAng,
        colAng,
        colMate
    );
    return dedupePolygonPoints([
        { x: rowOn.x, y: rowOn.y },
        ...periLeg.slice(1),
        { x: colOn.x, y: colOn.y },
        hubPt,
    ]);
};

/** 2×2 channel perimeter walls and/or a center cross that should split the hub void. */
const has2x2InteriorSplit = (tl, tr, bl, br, segments, markers, hub) => {
    const pairs = [
        [tl, tr],
        [bl, br],
        [tl, bl],
        [tr, br],
    ];
    if (
        pairs.every(([a, b]) =>
            findWallSegmentBetweenMarkers(a, b, segments, markers)
        )
    ) {
        return true;
    }
    const maxR = Math.max(
        getMarkerRadiusImagePx(tl),
        getMarkerRadiusImagePx(tr),
        getMarkerRadiusImagePx(bl),
        getMarkerRadiusImagePx(br)
    );
    const cross = findInteriorCrossWallsNearHub(
        hub,
        segments,
        markers,
        maxR * 2.5
    );
    return cross.vert.length > 0 && cross.horiz.length > 0;
};

/** Wall midline on the axis nearest the 2×2 hub (splits top/bottom or left/right cells). */
const wallMidlineClosestToHub = (wallEntries, hubCoord, horizontal) => {
    if (!wallEntries.length) return hubCoord;
    let best = wallEntries[0];
    let bestD = Infinity;
    wallEntries.forEach((w) => {
        const mid = horizontal
            ? (w.geom.y1 + w.geom.y2) / 2
            : (w.geom.x1 + w.geom.x2) / 2;
        const d = Math.abs(mid - hubCoord);
        if (d < bestD) {
            bestD = d;
            best = w;
        }
    });
    return horizontal
        ? (best.geom.y1 + best.geom.y2) / 2
        : (best.geom.x1 + best.geom.x2) / 2;
};

/**
 * Drop one merged interior face (graph `grid:*`, `wallloop`, `grid:bin`) that spans a 2×2
 * cross once four `wallquad:*` pockets are synthesized.
 */
const dropFacesObscuring2x2WallQuads = (faces, wallQuads) => {
    if (!wallQuads?.length) return faces;
    const maxQuadArea = Math.max(
        ...wallQuads.map((q) => q.area ?? Math.abs(polygonArea(q.polygon)))
    );
    return faces.filter((f) => {
        if (wallQuads.some((q) => q.id === f.id)) return true;
        if (!wallQuads.some((q) => facesOverlap(f, q))) return true;
        const quadsInside = wallQuads.filter((q) => {
            const c = q.centroid || polygonCentroid(q.polygon);
            return pointInPolygon(c.x, c.y, f.polygon);
        }).length;
        const fArea = f.area ?? Math.abs(polygonArea(f.polygon));
        if (quadsInside >= 2 && fArea > maxQuadArea * 1.12) return false;
        const id = String(f.id ?? "");
        const mids = markerIdsFromBinArcKeys(f.binArcKeys);
        if (
            quadsInside >= 1 &&
            fArea > maxQuadArea * 1.12 &&
            (id.startsWith("wallloop:") ||
                id.startsWith("grid:bin:") ||
                (id.startsWith("grid:") && !id.startsWith("grid:bin:")) ||
                mids.size >= 4)
        ) {
            return false;
        }
        if (quadsInside >= 3) return false;
        return true;
    });
};

/**
 * Interior regions inside a 2×2 wall cross (+): four quadrant cells plus a strip along
 * each wall toward the bin hub. Planar graph often merges the cross into one face when
 * arcs are also present.
 */
const synthesize2x2InteriorWallRegions = (markers, resolvedById, segments) => {
    const faces = [];
    const seen = new Set();

    find2x2BinBlocks(markers).forEach(({ tl, tr, bl, br }) => {
        const hub = {
            x: (tl.x + tr.x + bl.x + br.x) / 4,
            y: (tl.y + tr.y + bl.y + br.y) / 4,
        };
        const pairs = [
            [tl, tr],
            [bl, br],
            [tl, bl],
            [tr, br],
        ];
        const wallEntries = [];
        pairs.forEach(([a, b]) => {
            const hit = findWallSegmentBetweenMarkers(a, b, segments, markers);
            if (hit) wallEntries.push({ a, b, ...hit });
        });

        wallEntries.forEach(({ a, b, segId, geom }) => {
            const id = `wallstrip:${segId}:${[a.id, b.id].sort().join(",")}`;
            if (seen.has(id)) return;
            const poly = buildWallStripTowardHub(geom, a, b, hub, markers);
            if (!polygonValidForArcPocket(poly, markers, [a.id, b.id])) return;
            const cent = polygonCentroid(poly);
            if (!pointInPolygon(cent.x, cent.y, poly)) return;
            seen.add(id);
            faces.push({
                id,
                polygon: poly,
                centroid: cent,
                labelPoint: polygonLabelPoint(poly, markers),
                area: Math.abs(polygonArea(poly)),
                segmentIds: [segId],
                binArcKeys: [],
            });
        });

        if (wallEntries.length < 4) return;

        const wallForPair = (a, b) =>
            wallEntries.find(
                (w) =>
                    (w.a.id === a.id && w.b.id === b.id) ||
                    (w.a.id === b.id && w.b.id === a.id)
            );

        const topWall = wallForPair(tl, tr);
        const botWall = wallForPair(bl, br);
        const leftWall = wallForPair(tl, bl);
        const rightWall = wallForPair(tr, br);
        if (!topWall || !botWall || !leftWall || !rightWall) return;

        const channelVert = wallEntries.filter(
            (w) => !wallIsMostlyHorizontal(w.geom)
        );
        const channelHoriz = wallEntries.filter((w) =>
            wallIsMostlyHorizontal(w.geom)
        );
        if (channelVert.length < 2 || channelHoriz.length < 2) return;

        channelVert.sort(
            (a, b) =>
                (a.geom.x1 + a.geom.x2) / 2 - (b.geom.x1 + b.geom.x2) / 2
        );
        channelHoriz.sort(
            (a, b) =>
                (a.geom.y1 + a.geom.y2) / 2 - (b.geom.y1 + b.geom.y2) / 2
        );

        const maxR = Math.max(
            getMarkerRadiusImagePx(tl),
            getMarkerRadiusImagePx(tr),
            getMarkerRadiusImagePx(bl),
            getMarkerRadiusImagePx(br)
        );
        const cross = findInteriorCrossWallsNearHub(
            hub,
            segments,
            markers,
            maxR * 2.5
        );

        let xMid = hub.x;
        let yMid = hub.y;
        if (cross.vert.length) {
            xMid = wallMidlineClosestToHub(cross.vert, hub.x, false);
        }
        if (cross.horiz.length) {
            yMid = wallMidlineClosestToHub(cross.horiz, hub.y, true);
        }

        const pad = 1.5;
        const xL =
            (channelVert[0].geom.x1 + channelVert[0].geom.x2) / 2 + pad;
        const xR =
            (channelVert[channelVert.length - 1].geom.x1 +
                channelVert[channelVert.length - 1].geom.x2) /
                2 -
            pad;
        const yTop =
            (channelHoriz[0].geom.y1 + channelHoriz[0].geom.y2) / 2 + pad;
        const yBot =
            (channelHoriz[channelHoriz.length - 1].geom.y1 +
                channelHoriz[channelHoriz.length - 1].geom.y2) /
                2 -
            pad;
        if (xMid <= xL || xMid >= xR) xMid = (xL + xR) / 2;
        if (yMid <= yTop || yMid >= yBot) yMid = (yTop + yBot) / 2;

        const split = { xMid, yMid };
        const binKey = [tl.id, tr.id, bl.id, br.id].sort().join(",");
        const segIds = [
            ...new Set([
                ...wallEntries.map((w) => w.segId),
                ...cross.vert.map((w) => w.segId),
                ...cross.horiz.map((w) => w.segId),
            ]),
        ];

        const quadBuilds = [
            {
                corner: "tl",
                cornerBin: tl,
                rowMate: tr,
                colMate: bl,
                rowWall: topWall,
                colWall: leftWall,
                involved: [tl.id, tr.id, bl.id],
            },
            {
                corner: "tr",
                cornerBin: tr,
                rowMate: tl,
                colMate: br,
                rowWall: topWall,
                colWall: rightWall,
                involved: [tr.id, tl.id, br.id],
            },
            {
                corner: "bl",
                cornerBin: bl,
                rowMate: br,
                colMate: tl,
                rowWall: botWall,
                colWall: leftWall,
                involved: [bl.id, br.id, tl.id],
            },
            {
                corner: "br",
                cornerBin: br,
                rowMate: bl,
                colMate: tr,
                rowWall: botWall,
                colWall: rightWall,
                involved: [br.id, bl.id, tr.id],
            },
        ];

        quadBuilds.forEach(
            ({ corner, cornerBin, rowMate, colMate, rowWall, colWall, involved }) => {
                const id = `wallquad:${binKey}:${corner}`;
                if (seen.has(id)) return;
                const poly = build2x2QuadrantHubPocket(
                    cornerBin,
                    rowMate,
                    colMate,
                    rowWall,
                    colWall,
                    split,
                    markers
                );
                if (!poly?.length) return;
                if (!polygonValidForArcPocket(poly, markers, involved)) return;
                const cent = polygonCentroid(poly);
                if (!pointInPolygon(cent.x, cent.y, poly)) return;
                seen.add(id);
                faces.push({
                    id,
                    polygon: poly,
                    centroid: cent,
                    labelPoint: polygonLabelPoint(poly, markers),
                    area: Math.abs(polygonArea(poly)),
                    segmentIds: segIds,
                    binArcKeys: [],
                });
            }
        );
    });

    return faces;
};

const arcAngleOnMarker = (geom, marker) => {
    if (geom.markerId1 === marker.id && geom.angle1 != null) return geom.angle1;
    if (geom.markerId2 === marker.id && geom.angle2 != null) return geom.angle2;
    const x = geom.markerId1 === marker.id ? geom.x1 : geom.x2;
    const y = geom.markerId1 === marker.id ? geom.y1 : geom.y2;
    return Math.atan2(y - marker.y, x - marker.x);
};

const findArcBetweenMarkers = (mA, mB, resolvedById) => {
    let found = null;
    resolvedById.forEach((geom, segId) => {
        if (found || geom.kind !== "arc") return;
        const ids = new Set([geom.markerId1, geom.markerId2].filter(Boolean));
        if (ids.has(mA.id) && ids.has(mB.id)) found = { segId, geom };
    });
    return found;
};

const wallGeomTouchesMarker = (geom, marker, markers) => {
    if (!geom || !marker) return false;
    if (geom.markerId1 === marker.id || geom.markerId2 === marker.id) return true;
    const [a, b] = resolveWallBinAnchors(geom, markers);
    return a?.id === marker.id || b?.id === marker.id;
};

/**
 * Horizontal wall snapped directly between two bins in a row (top tl–tr or bottom bl–br).
 * Only a segment that connects both markers qualifies — loose "touches one bin" matches invent
 * phantom caps from site perimeter walls and shrink the pocket to a narrow strip.
 */
const findHorizontalWallCapForRowPair = (mLeft, mRight, segments, markers) => {
    const between = findWallSegmentBetweenMarkers(mLeft, mRight, segments, markers);
    if (!between?.geom || !wallIsMostlyHorizontal(between.geom)) return null;
    return between;
};

/**
 * True when horizontal caps sit inside the 2×2 hub void — not site perimeter walls along the
 * top/bottom of an entire bin row (B–C–D / F–G–H), which must not create a grid:wall pocket.
 */
const horizontalWallPairCapsInteriorHubVoid = (
    tl,
    tr,
    bl,
    br,
    topWallGeom,
    botWallGeom
) => {
    if (!topWallGeom || !botWallGeom) return false;
    const rTop = Math.max(
        getMarkerRadiusImagePx(tl),
        getMarkerRadiusImagePx(tr)
    );
    const rBot = Math.max(
        getMarkerRadiusImagePx(bl),
        getMarkerRadiusImagePx(br)
    );
    const rowTopY = (tl.y + tr.y) / 2;
    const rowBotY = (bl.y + br.y) / 2;
    const hubY = (rowTopY + rowBotY) / 2;
    const topY = (topWallGeom.y1 + topWallGeom.y2) / 2;
    const botY = (botWallGeom.y1 + botWallGeom.y2) / 2;

    if (topY <= rowTopY - rTop * 0.45) return false;
    if (botY >= rowBotY + rBot * 0.45) return false;
    if (topY >= hubY + rTop * 0.4) return false;
    if (botY <= hubY - rBot * 0.4) return false;

    const minGap = Math.min(rTop, rBot) * 0.35;
    if (botY - topY < minGap) return false;

    const hubPad = Math.min(rTop, rBot) * 0.12;
    const hubMinX = Math.min(tl.x, tr.x, bl.x, br.x) - hubPad;
    const hubMaxX = Math.max(tl.x, tr.x, bl.x, br.x) + hubPad;
    const hubMinY = Math.min(tl.y, tr.y, bl.y, br.y) - hubPad;
    const hubMaxY = Math.max(tl.y, tr.y, bl.y, br.y) + hubPad;
    const wallInsideHub = (geom) => {
        const mx = (geom.x1 + geom.x2) / 2;
        const my = (geom.y1 + geom.y2) / 2;
        return (
            mx >= hubMinX &&
            mx <= hubMaxX &&
            my >= hubMinY &&
            my <= hubMaxY
        );
    };
    return wallInsideHub(topWallGeom) && wallInsideHub(botWallGeom);
};

/**
 * 2×2 pocket capped by horizontal walls (area H) — top/bottom follow real wall lines, not
 * tilted chords between arc attachment points on adjacent bins.
 */
const build2x2HorizontalWallCentralPocketPolygon = (
    tl,
    tr,
    bl,
    br,
    topGeom,
    botGeom,
    markers
) => {
    const angTopTL = wallAngleOnMarker(topGeom, tl, markers);
    const angTopTR = wallAngleOnMarker(topGeom, tr, markers);
    const angBotBL = wallAngleOnMarker(botGeom, bl, markers);
    const angBotBR = wallAngleOnMarker(botGeom, br, markers);

    const topY = (topGeom.y1 + topGeom.y2) / 2;
    const botY = (botGeom.y1 + botGeom.y2) / 2;

    const pTopTL = pointOnBinPerimeter(tl, angTopTL);
    const pTopTR = pointOnBinPerimeter(tr, angTopTR);
    const pBotBL = pointOnBinPerimeter(bl, angBotBL);
    const pBotBR = pointOnBinPerimeter(br, angBotBR);

    const clipHorizontalWall = (geom, y, xMin, xMax) => {
        const wMinX = Math.min(geom.x1, geom.x2);
        const wMaxX = Math.max(geom.x1, geom.x2);
        const xL = Math.max(xMin, wMinX);
        const xR = Math.min(xMax, wMaxX);
        if (xR - xL < 1) return [];
        return [{ x: xL, y }, { x: xR, y }];
    };

    const topEdge = clipHorizontalWall(
        topGeom,
        topY,
        Math.min(pTopTL.x, pTopTR.x),
        Math.max(pTopTL.x, pTopTR.x)
    );
    const botEdge = clipHorizontalWall(
        botGeom,
        botY,
        Math.min(pBotBL.x, pBotBR.x),
        Math.max(pBotBL.x, pBotBR.x)
    );

    const rightLegTR = pickPerimeterLegTowardPartner(
        tr,
        angTopTR,
        partnerContactAngle(tr, br),
        br
    );
    const rightLegBR = pickPerimeterLegTowardPartner(
        br,
        partnerContactAngle(br, tr),
        angBotBR,
        tr
    );
    const leftLegBL = pickPerimeterLegTowardPartner(
        bl,
        angBotBL,
        partnerContactAngle(bl, tl),
        tl
    );
    const leftLegTL = pickPerimeterLegTowardPartner(
        tl,
        partnerContactAngle(tl, bl),
        angTopTL,
        bl
    );

    const topPts =
        topEdge.length >= 2 ? topEdge : [pTopTL, pTopTR];
    const botPts =
        botEdge.length >= 2 ? [...botEdge].reverse() : [pBotBR, pBotBL];

    return dedupePolygonPoints([
        ...topPts,
        ...rightLegTR.slice(1),
        ...rightLegBR.slice(1),
        ...botPts,
        ...leftLegBL.slice(1),
        ...leftLegTL.slice(1),
    ]);
};

const synthesize2x2WallRowCentralPockets = (markers, segments) => {
    const faces = [];
    const seen = new Set();
    find2x2BinHubGrids(markers).forEach((grid) => {
        const { tl, tr, bl, br } = grid;
        const hub = {
            x: (tl.x + tr.x + bl.x + br.x) / 4,
            y: (tl.y + tr.y + bl.y + br.y) / 4,
        };
        if (has2x2InteriorSplit(tl, tr, bl, br, segments, markers, hub)) return;

        const topWall = findHorizontalWallCapForRowPair(tl, tr, segments, markers);
        const botWall = findHorizontalWallCapForRowPair(bl, br, segments, markers);
        if (!topWall || !botWall) return;
        if (
            !horizontalWallPairCapsInteriorHubVoid(
                tl,
                tr,
                bl,
                br,
                topWall.geom,
                botWall.geom
            )
        ) {
            return;
        }

        const topY = (topWall.geom.y1 + topWall.geom.y2) / 2;
        const botY = (botWall.geom.y1 + botWall.geom.y2) / 2;

        const poly = build2x2HorizontalWallCentralPocketPolygon(
            tl,
            tr,
            bl,
            br,
            topWall.geom,
            botWall.geom,
            markers
        );
        if (!polygonValidForChannelPocket(poly, markers)) return;
        if (!pointInPolygon(hub.x, hub.y, poly)) return;

        const binPoly = build2x2BinCentralPocketPolygon(tl, tr, bl, br);
        const binArea = binPoly?.length
            ? Math.abs(polygonArea(binPoly))
            : 0;
        const wallArea = Math.abs(polygonArea(poly));
        if (binArea > 0 && wallArea < binArea * 0.72) return;

        const maxSpan =
            getMarkerRadiusImagePx(tl) +
            getMarkerRadiusImagePx(tr) +
            getMarkerRadiusImagePx(bl) +
            getMarkerRadiusImagePx(br);
        const bb = polygonBBox(poly);
        if (
            bb.maxX - bb.minX > maxSpan * 1.35 ||
            bb.maxY - bb.minY > maxSpan * 1.35
        ) {
            return;
        }

        const key = [tl.id, tr.id, bl.id, br.id].sort().join(",");
        if (seen.has(key)) return;
        seen.add(key);
        faces.push({
            id: `grid:wall:${key}`,
            polygon: poly,
            centroid: polygonCentroid(poly),
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [topWall.segId, botWall.segId],
            binArcKeys: [],
        });
    });
    return faces;
};

/**
 * Center pocket in a 2×2 bin block bounded by user arcs on top and bottom (areas C / X style).
 * Planar graph often misses this loop when arcs replace straight channel walls.
 */
const build2x2ArcCentralPocketPolygon = (tl, tr, bl, br, topArc, botArc, markers) => {
    const angTL = arcAngleOnMarker(topArc.geom, tl);
    const angTR = arcAngleOnMarker(topArc.geom, tr);
    const angBL = arcAngleOnMarker(botArc.geom, bl);
    const angBR = arcAngleOnMarker(botArc.geom, br);

    const pTL = pointOnBinPerimeter(tl, angTL);
    const pTR = pointOnBinPerimeter(tr, angTR);
    const pBL = pointOnBinPerimeter(bl, angBL);
    const pBR = pointOnBinPerimeter(br, angBR);

    const legTR = pickPerimeterLegTowardPartner(
        tr,
        angTR,
        Math.atan2(br.y - tr.y, br.x - tr.x),
        br
    );
    const legBR = pickPerimeterLegTowardPartner(
        br,
        angBR,
        Math.atan2(bl.y - br.y, bl.x - br.x),
        bl
    );
    const legBL = pickPerimeterLegTowardPartner(
        bl,
        angBL,
        Math.atan2(tl.y - bl.y, tl.x - bl.x),
        tl
    );
    const legTL = pickPerimeterLegTowardPartner(
        tl,
        angTL,
        Math.atan2(tr.y - tl.y, tr.x - tl.x),
        tr
    );

    return dedupePolygonPoints([
        pTL,
        pTR,
        ...legTR.slice(1),
        ...legBR.slice(1),
        pBR,
        pBL,
        ...legBL.slice(1),
        ...legTL.slice(1),
    ]);
};

/**
 * Curved-polygon void enclosed by mutually-touching bins. Walk the bins in CCW order
 * around their hub; for each bin trace the hub-facing perimeter arc from its contact
 * point with the previous bin to its contact with the next bin. Consecutive arcs meet
 * exactly at the shared tangent contact points, so no chord ever crosses a bin interior
 * (straight chords were the source of fill bleeding into the round bins).
 */
const buildBinHubGapPolygon = (bins) => {
    if (!bins || bins.length < 3) return [];
    const hub = {
        x: bins.reduce((s, m) => s + m.x, 0) / bins.length,
        y: bins.reduce((s, m) => s + m.y, 0) / bins.length,
    };
    const ordered = [...bins].sort(
        (p, q) =>
            Math.atan2(p.y - hub.y, p.x - hub.x) -
            Math.atan2(q.y - hub.y, q.x - hub.x)
    );
    const pts = [];
    for (let i = 0; i < ordered.length; i++) {
        const bin = ordered[i];
        const prev = ordered[(i + ordered.length - 1) % ordered.length];
        const next = ordered[(i + 1) % ordered.length];
        const leg = pickPerimeterLegTowardPartner(
            bin,
            partnerContactAngle(bin, prev),
            partnerContactAngle(bin, next),
            hub
        );
        if (i === 0) pts.push(...leg);
        else pts.push(...leg.slice(1));
    }
    return dedupePolygonPoints(pts);
};

/**
 * Center void in a 2×2 bin block with no top/bottom arcs (area H).
 * Planar graph often loses this loop once other arcs are added to the site.
 */
const build2x2BinCentralPocketPolygon = (tl, tr, bl, br) =>
    buildBinHubGapPolygon([tl, tr, bl, br]);

const synthesize2x2BinCentralPockets = (markers, segments) => {
    const faces = [];
    const seen = new Set();
    find2x2BinHubGrids(markers).forEach((grid) => {
        const { tl, tr, bl, br } = grid;
        const hub = {
            x: (tl.x + tr.x + bl.x + br.x) / 4,
            y: (tl.y + tr.y + bl.y + br.y) / 4,
        };
        if (has2x2InteriorSplit(tl, tr, bl, br, segments, markers, hub)) return;
        const key = [tl.id, tr.id, bl.id, br.id].sort().join(",");
        if (seen.has(key)) return;
        const sf = build2x2BinCentralPocketFace(
            tl,
            tr,
            bl,
            br,
            markers,
            segments,
            new Set()
        );
        if (!sf) return;
        seen.add(key);
        faces.push(sf);
    });
    return faces;
};

/** True when three bins are corners of a 2×2 block that should have one hub pocket. */
const threeBinsBelongToUnsplit2x2Hub = (bins, markers, segments) => {
    const ids = new Set(bins.map((b) => b.id));
    if (ids.size !== 3) return false;
    for (const grid of find2x2BinHubGrids(markers)) {
        const blockIds = [grid.tl.id, grid.tr.id, grid.bl.id, grid.br.id];
        if (blockIds.filter((id) => ids.has(id)).length !== 3) continue;
        const hub = {
            x: (grid.tl.x + grid.tr.x + grid.bl.x + grid.br.x) / 4,
            y: (grid.tl.y + grid.tr.y + grid.bl.y + grid.br.y) / 4,
        };
        if (
            has2x2InteriorSplit(
                grid.tl,
                grid.tr,
                grid.bl,
                grid.br,
                segments,
                markers,
                hub
            )
        ) {
            continue;
        }
        const poly = build2x2BinCentralPocketPolygon(
            grid.tl,
            grid.tr,
            grid.bl,
            grid.br
        );
        if (!poly?.length) continue;
        const triHub = {
            x: bins.reduce((s, m) => s + m.x, 0) / bins.length,
            y: bins.reduce((s, m) => s + m.y, 0) / bins.length,
        };
        if (pointInPolygon(triHub.x, triHub.y, poly)) return true;
    }
    return false;
};

/** Four-bin hub pocket for a snapped 2×2 block (null when invalid or wall-split). */
const build2x2BinCentralPocketFace = (
    tl,
    tr,
    bl,
    br,
    markers,
    segments,
    userSegmentIds
) => {
    const involved = [tl.id, tr.id, bl.id, br.id];
    const hub = {
        x: (tl.x + tr.x + bl.x + br.x) / 4,
        y: (tl.y + tr.y + bl.y + br.y) / 4,
    };
    const poly = build2x2BinCentralPocketPolygon(tl, tr, bl, br);
    if (!poly?.length) return null;
    if (!polygonValidFor2x2BinHubPocket(poly, markers, involved)) return null;
    if (!pointInPolygon(hub.x, hub.y, poly)) return null;
    const key = involved.sort().join(",");
    const sf = {
        id: `grid:bin:${key}`,
        polygon: poly,
        centroid: polygonCentroid(poly),
        labelPoint: polygonLabelPoint(poly, markers),
        area: Math.abs(polygonArea(poly)),
        segmentIds: [],
        binArcKeys: [],
    };
    if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return null;
    return sf;
};

/** Min/max ring length for bin-perimeter hub gaps (triangles through hex voids). */
const HUB_GAP_MIN_CYCLE = 3;
const HUB_GAP_MAX_CYCLE = 8;

/** Snap test for hub-gap rings — slightly looser than perimeter snap so staggered grids connect. */
const binsAdjacentForHubGapCycle = (m1, m2) => {
    if (binsAreSnappedTogether(m1, m2)) return true;
    const r1 = getMarkerRadiusImagePx(m1);
    const r2 = getMarkerRadiusImagePx(m2);
    const d = Math.hypot(m2.x - m1.x, m2.y - m1.y);
    if (d < 1e-3) return false;
    const gap = d - (r1 + r2);
    return gap <= BIN_SNAP_ADJACENT_TOL_PX * 2 && gap >= -BIN_TOUCH_TOL_PX * 2;
};

const buildBinSnapAdjacency = (markers) => {
    const adj = new Map();
    (markers || []).forEach((m) => adj.set(m.id, []));
    const list = markers || [];
    for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
            const a = list[i];
            const b = list[j];
            if (!binsAdjacentForHubGapCycle(a, b)) continue;
            adj.get(a.id).push(b.id);
            adj.get(b.id).push(a.id);
        }
    }
    return adj;
};

/** Simple cycles in the snapped-bin graph (3-cliques are one case; 4-rings cover central voids). */
const findBinSnapCycles = (markers, adj, minLen, maxLen) => {
    const markerById = new Map((markers || []).map((m) => [m.id, m]));
    const seen = new Set();
    const cycles = [];

    const visit = (startId, curId, pathIds, blocked) => {
        if (pathIds.length > maxLen) return;
        for (const nextId of adj.get(curId) || []) {
            if (nextId === startId) {
                if (pathIds.length >= minLen) {
                    const key = [...pathIds].sort().join(",");
                    if (!seen.has(key)) {
                        seen.add(key);
                        cycles.push(pathIds.map((id) => markerById.get(id)));
                    }
                }
                continue;
            }
            if (blocked.has(nextId)) continue;
            blocked.add(nextId);
            visit(startId, nextId, [...pathIds, nextId], blocked);
            blocked.delete(nextId);
        }
    };

    (markers || []).forEach((start) => {
        visit(start.id, start.id, [start.id], new Set([start.id]));
    });
    return cycles;
};

/**
 * Curved voids bounded by bin perimeters — triangles (A/B) and larger rings (e.g. 4-bin hub).
 * Requires a snap-graph cycle, not a full clique, so staggered grids still get central pockets.
 */
const synthesizeBinHubGapPockets = (markers, segments = []) => {
    const adj = buildBinSnapAdjacency(markers);
    const cycles = findBinSnapCycles(
        markers,
        adj,
        HUB_GAP_MIN_CYCLE,
        HUB_GAP_MAX_CYCLE
    );
    const faces = [];
    const seenIds = new Set();

    cycles.forEach((bins) => {
        const key = bins.map((b) => b.id).sort().join(",");
        const id = bins.length === 3 ? `gap:tri:${key}` : `gap:hub:${key}`;
        if (seenIds.has(id)) return;
        if (
            bins.length === 3 &&
            threeBinsBelongToUnsplit2x2Hub(bins, markers, segments)
        ) {
            return;
        }
        if (bins.length === 4) {
            const blockKey = key;
            for (const grid of find2x2BinHubGrids(markers)) {
                const gridKey = [grid.tl.id, grid.tr.id, grid.bl.id, grid.br.id]
                    .sort()
                    .join(",");
                if (gridKey !== blockKey) continue;
                const hub = {
                    x: (grid.tl.x + grid.tr.x + grid.bl.x + grid.br.x) / 4,
                    y: (grid.tl.y + grid.tr.y + grid.bl.y + grid.br.y) / 4,
                };
                if (
                    !has2x2InteriorSplit(
                        grid.tl,
                        grid.tr,
                        grid.bl,
                        grid.br,
                        segments,
                        markers,
                        hub
                    )
                ) {
                    return;
                }
            }
        }
        const poly = buildBinHubGapPolygon(bins);
        const involved = bins.map((b) => b.id);
        if (!polygonValidForArcPocket(poly, markers, involved)) return;
        const hub = {
            x: bins.reduce((s, m) => s + m.x, 0) / bins.length,
            y: bins.reduce((s, m) => s + m.y, 0) / bins.length,
        };
        if (!pointInPolygon(hub.x, hub.y, poly)) return;
        if (
            bins.some(
                (m) =>
                    Math.hypot(hub.x - m.x, hub.y - m.y) <
                    getMarkerRadiusImagePx(m) * 0.82
            )
        ) {
            return;
        }
        seenIds.add(id);
        faces.push({
            id,
            polygon: poly,
            centroid: polygonCentroid(poly),
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [],
            binArcKeys: [],
        });
    });
    return faces;
};

/** @deprecated Use synthesizeBinHubGapPockets — kept as alias for callers. */
const synthesizeTripleBinGapPockets = synthesizeBinHubGapPockets;

/** Sample the minor (≤180°) perimeter arc between two angles on a bin. */
const sampleMinorArcPts = (m, a1, a2, steps = 10) => {
    const r = getMarkerRadiusImagePx(m);
    let d = normalizeAngle(a2) - normalizeAngle(a1);
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    const base = normalizeAngle(a1);
    const pts = [];
    for (let i = 0; i <= steps; i++) {
        const t = base + (d * i) / steps;
        pts.push({ x: m.x + r * Math.cos(t), y: m.y + r * Math.sin(t) });
    }
    return pts;
};

/** Foot of point (px,py) on a segment; returns clamped foot, unclamped param t, distance. */
const pointSegFoot = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-6) {
        return { x: x1, y: y1, t: 0, dist: Math.hypot(px - x1, py - y1) };
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / len2;
    const tc = Math.max(0, Math.min(1, t));
    const fx = x1 + tc * dx;
    const fy = y1 + tc * dy;
    return { x: fx, y: fy, t, dist: Math.hypot(px - fx, py - fy) };
};

/**
 * Find a straight wall segment that "caps" the opening between two outer bins A and B
 * (both close to the wall, on the outward side, wall roughly perpendicular to the
 * outward direction). Returns the wall id and the feet of A/B on the wall, or null.
 */
const findCapWallForPair = (A, B, outx, outy, resolvedById) => {
    if (!resolvedById) return null;
    const rA = getMarkerRadiusImagePx(A);
    const rB = getMarkerRadiusImagePx(B);
    let best = null;
    resolvedById.forEach((geom, segId) => {
        if (!geom || geom.kind === "arc") return;
        if (geom.x1 == null || geom.x2 == null) return;
        const fA = pointSegFoot(A.x, A.y, geom.x1, geom.y1, geom.x2, geom.y2);
        const fB = pointSegFoot(B.x, B.y, geom.x1, geom.y1, geom.x2, geom.y2);
        if (fA.t < -0.05 || fA.t > 1.05 || fB.t < -0.05 || fB.t > 1.05) return;
        if (fA.dist > rA * 1.7 || fB.dist > rB * 1.7) return;
        const dirA = (fA.x - A.x) * outx + (fA.y - A.y) * outy;
        const dirB = (fB.x - B.x) * outx + (fB.y - B.y) * outy;
        if (dirA <= 0 || dirB <= 0) return;
        let wdx = geom.x2 - geom.x1;
        let wdy = geom.y2 - geom.y1;
        const wl = Math.hypot(wdx, wdy) || 1;
        wdx /= wl;
        wdy /= wl;
        if (Math.abs(wdx * outx + wdy * outy) > 0.6) return;
        const score = fA.dist + fB.dist;
        if (!best || score < best.score) {
            best = {
                segId,
                footA: { x: fA.x, y: fA.y },
                footB: { x: fB.x, y: fB.y },
                score,
            };
        }
    });
    return best;
};

/**
 * Void between a hub bin and two of its neighbors that do NOT touch each other.
 * If a wall caps the opening, the pocket extends up to that wall (the wall is the open
 * boundary); otherwise the opening is closed by the throat line between the outer bins.
 * Boundary = hub throat-facing arc → B arc → (wall or throat) → A arc.
 */
const buildHubThroatPolygon = (hub, A, B, resolvedById) => {
    const rA = getMarkerRadiusImagePx(A);
    const rB = getMarkerRadiusImagePx(B);
    const aHA = Math.atan2(A.y - hub.y, A.x - hub.x);
    const aHB = Math.atan2(B.y - hub.y, B.x - hub.x);
    const aBH = Math.atan2(hub.y - B.y, hub.x - B.x);
    const aAH = Math.atan2(hub.y - A.y, hub.x - A.x);

    const aAB = Math.atan2(B.y - A.y, B.x - A.x);
    const aBA = Math.atan2(A.y - B.y, A.x - B.x);
    const TA = { x: A.x + rA * Math.cos(aAB), y: A.y + rA * Math.sin(aAB) };
    const TB = { x: B.x + rB * Math.cos(aBA), y: B.y + rB * Math.sin(aBA) };
    const throatMid = { x: (TA.x + TB.x) / 2, y: (TA.y + TB.y) / 2 };
    let outx = throatMid.x - hub.x;
    let outy = throatMid.y - hub.y;
    const ol = Math.hypot(outx, outy) || 1;
    outx /= ol;
    outy /= ol;

    const wall = findCapWallForPair(A, B, outx, outy, resolvedById);

    const pts = [];
    sampleMinorArcPts(hub, aHA, aHB).forEach((p) => pts.push(p));
    if (wall) {
        const angBW = Math.atan2(wall.footB.y - B.y, wall.footB.x - B.x);
        const angAW = Math.atan2(wall.footA.y - A.y, wall.footA.x - A.x);
        sampleMinorArcPts(B, aBH, angBW).forEach((p) => pts.push(p));
        pts.push(wall.footB);
        pts.push(wall.footA);
        sampleMinorArcPts(A, angAW, aAH).forEach((p) => pts.push(p));
    } else {
        sampleMinorArcPts(B, aBH, aBA).forEach((p) => pts.push(p));
        sampleMinorArcPts(A, aAB, aAH).forEach((p) => pts.push(p));
    }
    return { poly: dedupePolygonPoints(pts), wallId: wall ? wall.segId : null };
};

/**
 * Interstitial voids around a hub bin: for each angularly-adjacent pair of neighbors that
 * do not touch each other, the gap between them is an area — extended to a capping wall
 * when present, otherwise closed by the bins' throat line.
 * Implements the rule "a round bin between surrounding bins encloses areas on every side".
 */
const synthesizeHubThroatPockets = (markers, resolvedById) => {
    const list = markers || [];
    const faces = [];
    const seen = new Set();
    const MAX_HUB_GAP_RAD = (135 * Math.PI) / 180;

    list.forEach((hub) => {
        if (getMarkerRadiusImagePx(hub) < 4) return;
        const withAngle = list
            .filter((m) => m.id !== hub.id && binsAreSnappedTogether(hub, m))
            .map((m) => ({ m, a: normalizeAngle(Math.atan2(m.y - hub.y, m.x - hub.x)) }))
            .sort((p, q) => p.a - q.a);
        const n = withAngle.length;
        if (n < 2) return;

        for (let i = 0; i < n; i++) {
            const A = withAngle[i].m;
            const B = withAngle[(i + 1) % n].m;
            let gap = withAngle[(i + 1) % n].a - withAngle[i].a;
            if (gap < 0) gap += 2 * Math.PI;
            if (gap > MAX_HUB_GAP_RAD) continue;
            if (binsAreSnappedTogether(A, B)) continue;

            const key = `${hub.id}|${[A.id, B.id].sort().join(",")}`;
            if (seen.has(key)) continue;

            const { poly, wallId } = buildHubThroatPolygon(hub, A, B, resolvedById);
            if (!poly || poly.length < 3) continue;
            const involved = [hub.id, A.id, B.id];
            if (!polygonValidForArcPocket(poly, markers, involved)) continue;

            const probe = polygonLabelPoint(poly, markers);
            if (!pointInPolygon(probe.x, probe.y, poly)) continue;
            if (
                involved.some((id) => {
                    const m = list.find((x) => x.id === id);
                    return (
                        m &&
                        Math.hypot(probe.x - m.x, probe.y - m.y) <
                            getMarkerRadiusImagePx(m) * 0.85
                    );
                })
            ) {
                continue;
            }

            seen.add(key);
            faces.push({
                id: `gap:throat:${key}`,
                polygon: poly,
                centroid: polygonCentroid(poly),
                labelPoint: probe,
                area: Math.abs(polygonArea(poly)),
                segmentIds: wallId ? [wallId] : [],
                binArcKeys: [],
            });
        }
    });
    return faces;
};

/** Discover 2×2 bin blocks (four mutually snapped bins in a ring — any orientation). */
const find2x2BinBlocks = (markers) => {
    const blocks = [];
    const seen = new Set();
    const adj = buildBinSnapAdjacency(markers);
    findBinSnapCycles(markers, adj, 4, 4).forEach((bins) => {
        const grid = orderMarkersAs2x2(bins[0], bins[1], bins[2], bins[3]);
        if (!grid) return;
        const key = [grid.tl.id, grid.tr.id, grid.bl.id, grid.br.id]
            .sort()
            .join(",");
        if (seen.has(key)) return;
        seen.add(key);
        blocks.push(grid);
    });
    return blocks;
};

/** 2×2 hub grids: snapped four-cycles (axis-aligned, diagonal, rotated rows). */
const find2x2BinHubGrids = (markers) => find2x2BinBlocks(markers);

const synthesize2x2ArcCentralPockets = (markers, resolvedById, segments) => {
    const faces = [];
    find2x2BinHubGrids(markers).forEach((grid) => {
        const { tl, tr, bl, br } = grid;
        const hub = {
            x: (tl.x + tr.x + bl.x + br.x) / 4,
            y: (tl.y + tr.y + bl.y + br.y) / 4,
        };
        if (has2x2InteriorSplit(tl, tr, bl, br, segments, markers, hub)) return;
        const topWall = findHorizontalWallCapForRowPair(tl, tr, segments, markers);
        const botWall = findHorizontalWallCapForRowPair(bl, br, segments, markers);
        if (
            topWall &&
            botWall &&
            horizontalWallPairCapsInteriorHubVoid(
                tl,
                tr,
                bl,
                br,
                topWall.geom,
                botWall.geom
            )
        ) {
            return;
        }
        const topArc = findArcBetweenMarkers(tl, tr, resolvedById);
        const botArc = findArcBetweenMarkers(bl, br, resolvedById);
        if (!topArc || !botArc) return;

        const poly = build2x2ArcCentralPocketPolygon(
            tl,
            tr,
            bl,
            br,
            topArc,
            botArc,
            markers
        );
        if (!polygonValidForChannelPocket(poly, markers)) return;

        const cx = (tl.x + tr.x + bl.x + br.x) / 4;
        const cy = (tl.y + tr.y + bl.y + br.y) / 4;
        if (!pointInPolygon(cx, cy, poly)) return;

        const maxSpan =
            getMarkerRadiusImagePx(tl) +
            getMarkerRadiusImagePx(tr) +
            getMarkerRadiusImagePx(bl) +
            getMarkerRadiusImagePx(br);
        const bb = polygonBBox(poly);
        if (
            bb.maxX - bb.minX > maxSpan * 1.2 ||
            bb.maxY - bb.minY > maxSpan * 1.2
        ) {
            return;
        }

        faces.push({
            id: `grid:${tl.id}:${tr.id}:${bl.id}:${br.id}`,
            polygon: poly,
            centroid: polygonCentroid(poly),
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [topArc.segId, botArc.segId],
            binArcKeys: [],
        });
    });
    return faces;
};

/** Arc polyline from the endpoint on `bin` toward the arc bulge (channel-side cap). */
const trimArcFromBinEndpoint = (arcPts, pArc, geom) => {
    let bestI = 0;
    let bestD = Infinity;
    arcPts.forEach((p, i) => {
        const d = Math.hypot(p.x - pArc.x, p.y - pArc.y);
        if (d < bestD) {
            bestD = d;
            bestI = i;
        }
    });
    const fwd = arcPts.slice(bestI);
    const back = arcPts.slice(0, bestI + 1).reverse();
    return scorePathOnBulgeSide(fwd, geom) >= scorePathOnBulgeSide(back, geom)
        ? fwd
        : back;
};

const cornerPocketAcceptsPolygon = (poly, bin, wallGeom, arcGeom, markers, channelProbe) => {
    if (!poly || poly.length < 3) return false;
    if (Math.abs(polygonArea(poly)) < 80) return false;
    if (pointInPolygon(bin.x, bin.y, poly)) return false;
    if (polygonIntrudesBinDisk(poly, bin, 2)) return false;
    const c = polygonCentroid(poly);
    if (!pointInPolygon(c.x, c.y, poly)) return false;
    const testArc = arcInteriorTestPoint(arcGeom);
    const wmx = (wallGeom.x1 + wallGeom.x2) / 2;
    const wmy = (wallGeom.y1 + wallGeom.y2) / 2;
    if (pointInPolygon(testArc.x, testArc.y, poly)) return true;
    if (pointInPolygon(wmx, wmy, poly)) return true;
    if (channelProbe && pointInPolygon(channelProbe.x, channelProbe.y, poly)) {
        return true;
    }
    return false;
};

/**
 * Triangular pocket outside the channel (O) where a bin meets a vertical wall and top/bottom arc.
 */
const buildBinWallArcCornerPocket = (bin, wallGeom, arcGeom, markers, channelProbe) => {
    const angArc = arcAngleOnMarker(arcGeom, bin);
    const angWall = wallAngleOnMarker(wallGeom, bin, markers);
    const arcPartner =
        arcGeom.markerId1 === bin.id
            ? markers.find((m) => m.id === arcGeom.markerId2)
            : markers.find((m) => m.id === arcGeom.markerId1);
    const pWall = pointOnBinPerimeter(bin, angWall);
    const pArc = pointOnBinPerimeter(bin, angArc);
    const arcSlice = trimArcFromBinEndpoint(sampleUserArc(arcGeom, 28), pArc, arcGeom);

    let best = null;
    let bestArea = 0;
    const tryPoly = (poly) => {
        if (!cornerPocketAcceptsPolygon(poly, bin, wallGeom, arcGeom, markers, channelProbe)) {
            return;
        }
        const area = Math.abs(polygonArea(poly));
        if (area > bestArea) {
            bestArea = area;
            best = poly;
        }
    };

    const legPicks = [
        (a, b) => pickPerimeterLegTowardPartner(bin, a, b, channelProbe),
        (a, b) => pickBinLegAlongBulgeSide(bin, a, b, arcGeom),
        (a, b) =>
            pickPerimeterLegTowardPartner(
                bin,
                a,
                b,
                arcPartner || channelProbe
            ),
    ];
    for (const pick of legPicks) {
        for (const [aFrom, aTo] of [
            [angArc, angWall],
            [angWall, angArc],
        ]) {
            const leg = pick(aFrom, aTo);
            tryPoly(
                dedupePolygonPoints([
                    ...arcSlice,
                    ...leg.slice(1),
                    pWall,
                    pArc,
                ])
            );
            tryPoly(
                dedupePolygonPoints([
                    pArc,
                    ...leg.slice(1),
                    pWall,
                    ...arcSlice.slice(1),
                ])
            );
        }
    }
    return best;
};

/** Four corners outside channel O (bin + vertical wall + top/bottom arc). */
const synthesizeChannelQuadCornerPockets = (markers, resolvedById) => {
    const grid = findWallChannelGrid(markers, resolvedById);
    if (!grid) return [];

    const topArc = findArcBetweenMarkers(grid.tl, grid.tr, resolvedById);
    const botArc = findArcBetweenMarkers(grid.bl, grid.br, resolvedById);
    if (!topArc || !botArc) return [];

    const channelCx = (grid.tl.x + grid.tr.x + grid.bl.x + grid.br.x) / 4;
    const channelCy = (grid.tl.y + grid.tr.y + grid.bl.y + grid.br.y) / 4;
    const channelProbe = { x: channelCx, y: channelCy };

    const specs = [
        { key: "tl", bin: grid.tl, wall: grid.leftGeom, wallId: grid.leftSegId, arc: topArc },
        { key: "tr", bin: grid.tr, wall: grid.rightGeom, wallId: grid.rightSegId, arc: topArc },
        { key: "bl", bin: grid.bl, wall: grid.leftGeom, wallId: grid.leftSegId, arc: botArc },
        { key: "br", bin: grid.br, wall: grid.rightGeom, wallId: grid.rightSegId, arc: botArc },
    ];

    const faces = [];
    specs.forEach(({ key, bin, wall, wallId, arc }) => {
        const poly = buildBinWallArcCornerPocket(
            bin,
            wall,
            arc.geom,
            markers,
            channelProbe
        );
        if (!poly) return;
        faces.push({
            id: `corner:${key}:${wallId}:${arc.segId}`,
            polygon: poly,
            centroid: polygonCentroid(poly),
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [wallId, arc.segId],
            binArcKeys: [],
        });
    });
    return faces;
};

const synthesizeWallChannelPockets = (markers, resolvedById, segments) => {
    const grid = findWallChannelGrid(markers, resolvedById);
    if (!grid) return [];

    const rect = tryChannelRectFromHorizontalWalls(grid, markers, resolvedById);
    if (rect) return [rect];

    const poly = buildWallChannelPocketPolygon(
        grid.tl,
        grid.tr,
        grid.bl,
        grid.br,
        grid.leftGeom,
        grid.rightGeom,
        markers
    );
    if (!polygonValidForChannelPocket(poly, markers)) return [];

    return [
        {
            id: `channel:${grid.leftSegId}:${grid.rightSegId}`,
            polygon: poly,
            centroid: polygonCentroid(poly),
            labelPoint: polygonLabelPoint(poly, markers),
            area: Math.abs(polygonArea(poly)),
            segmentIds: [grid.leftSegId, grid.rightSegId],
            binArcKeys: [],
        },
    ];
};

const layoutMarkerClusterCenterPx = (markers) => {
    if (!markers?.length) return null;
    let sx = 0;
    let sy = 0;
    markers.forEach((m) => {
        sx += m.x;
        sy += m.y;
    });
    return { x: sx / markers.length, y: sy / markers.length };
};

const exteriorProbeForBin = (bin, markers) => {
    const c = layoutMarkerClusterCenterPx(markers);
    if (!c) {
        return { x: bin.x + getMarkerRadiusImagePx(bin), y: bin.y };
    }
    const dx = bin.x - c.x;
    const dy = bin.y - c.y;
    const len = Math.hypot(dx, dy) || 1;
    const push = getMarkerRadiusImagePx(bin) * 1.25;
    return { x: bin.x + (dx / len) * push, y: bin.y + (dy / len) * push };
};

/** Small wall+arc+bin corners on the site perimeter (outside a 2×2 channel grid). */
const synthesizePerimeterCornerPockets = (markers, resolvedById) => {
    const faces = [];
    const seen = new Set();
    (markers || []).forEach((bin) => {
        const walls = [];
        const arcs = [];
        resolvedById.forEach((geom, segId) => {
            const touches =
                geom.markerId1 === bin.id || geom.markerId2 === bin.id;
            if (!touches) return;
            if (geom.kind === "wall") walls.push({ segId, geom });
            else if (geom.kind === "arc" && geom.curveX != null) {
                arcs.push({ segId, geom });
            }
        });
        if (!walls.length || !arcs.length) return;
        const probe = exteriorProbeForBin(bin, markers);
        walls.forEach((w) => {
            arcs.forEach((a) => {
                const key = `outer:${bin.id}:${w.segId}:${a.segId}`;
                if (seen.has(key)) return;
                const poly = buildBinWallArcCornerPocket(
                    bin,
                    w.geom,
                    a.geom,
                    markers,
                    probe
                );
                if (!poly) return;
                seen.add(key);
                faces.push({
                    id: key,
                    polygon: poly,
                    centroid: polygonCentroid(poly),
                    labelPoint: polygonLabelPoint(poly, markers),
                    area: Math.abs(polygonArea(poly)),
                    segmentIds: [w.segId, a.segId],
                    binArcKeys: [],
                });
            });
        });
    });
    return faces;
};

const polygonArea = (pts) => {
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    return a / 2;
};

const polygonCentroid = (pts) => {
    let cx = 0;
    let cy = 0;
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const cross = pts[i].x * pts[j].y - pts[j].x * pts[i].y;
        cx += (pts[i].x + pts[j].x) * cross;
        cy += (pts[i].y + pts[j].y) * cross;
        a += cross;
    }
    if (Math.abs(a) < 1e-6) {
        const sx = pts.reduce((s, p) => s + p.x, 0);
        const sy = pts.reduce((s, p) => s + p.y, 0);
        return { x: sx / pts.length, y: sy / pts.length };
    }
    a *= 0.5;
    return { x: cx / (6 * a), y: cy / (6 * a) };
};

const minDistToPolygonEdges = (px, py, poly) => {
    let best = Infinity;
    for (let i = 0; i < poly.length; i++) {
        const j = (i + 1) % poly.length;
        best = Math.min(
            best,
            distToSegment(px, py, poly[i].x, poly[i].y, poly[j].x, poly[j].y)
        );
    }
    return best;
};

/**
 * Best tag position inside a polygon — centered in the region, not hugging edges.
 * Narrow pockets (e.g. wall+arc corners) use bbox center; larger regions use inset search.
 */
export const polygonLabelPoint = (poly, markers) => {
    if (!poly?.length) return { x: 0, y: 0 };

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    poly.forEach((p) => {
        minX = Math.min(minX, p.x);
        maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y);
        maxY = Math.max(maxY, p.y);
    });
    const bx = (minX + maxX) / 2;
    const by = (minY + maxY) / 2;
    if (pointInPolygon(bx, by, poly)) {
        return { x: bx, y: by };
    }

    const centroid = polygonCentroid(poly);
    if (pointInPolygon(centroid.x, centroid.y, poly)) {
        const inset = minDistToPolygonEdges(centroid.x, centroid.y, poly);
        if (inset >= 8) return centroid;
    }

    const span = Math.max(maxX - minX, maxY - minY, 1);
    const step = Math.max(4, Math.min(10, span / 16));

    let bestPt = centroid;
    let bestScore = -Infinity;
    for (let x = minX; x <= maxX; x += step) {
        for (let y = minY; y <= maxY; y += step) {
            if (!pointInPolygon(x, y, poly)) continue;
            const edgeInset = minDistToPolygonEdges(x, y, poly);
            const centerBias = Math.hypot(x - bx, y - by);
            const score = edgeInset - centerBias * 0.12;
            if (score > bestScore) {
                bestScore = score;
                bestPt = { x, y };
            }
        }
    }

    if (pointInPolygon(bestPt.x, bestPt.y, poly) && bestScore > -Infinity) return bestPt;

    return centroid;
};

export const pointInPolygon = (x, y, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x;
        const yi = poly[i].y;
        const xj = poly[j].x;
        const yj = poly[j].y;
        const intersect =
            yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
};

const faceSignature = (segmentIds, binArcKeys) =>
    [...segmentIds].sort().join(",") + "|" + [...binArcKeys].sort().join(",");

const isFlatToolFace = (face, segments) => {
    if (String(face?.id).startsWith("flat:")) return true;
    if (!Array.isArray(segments) || !segments.length) return false;
    return !!closedRectIdForFace(face, segments);
};

const userSegmentCountOnFace = (face, userSegmentIds) => {
    if (!(userSegmentIds instanceof Set)) return 0;
    return (face.segmentIds || []).filter((id) => userSegmentIds.has(id)).length;
};

/**
 * Real gap/stice between multiple bins (not a bogus graph fill through bin interiors).
 * Labels may sit near a bin center in tight 2×2/2×3 grids but must still appear in the panel.
 */
const faceIsLegitimateMultiBinGap = (face, markers) => {
    if (faceIdIsSynthesizedPocket(face)) return true;
    const mids = markerIdsFromBinArcKeys(face.binArcKeys);
    if (mids.size >= 2) return true;
    if ((face.segmentIds || []).length > 0) {
        if (!face.polygon?.length) return true;
        return !markers?.some((m) => pointInPolygon(m.x, m.y, face.polygon));
    }
    return false;
};

/** Same rule as Site 3D buildSticeBinDraft — pocket center must not sit inside a round bin. */
const faceCoreInsideRoundBinMarker = (face, markers) => {
    if (!markers?.length) return false;
    const pt =
        face.labelPoint ||
        face.centroid ||
        (face.polygon?.length ? polygonCentroid(face.polygon) : null);
    if (!pt || !Number.isFinite(pt.x)) return false;
    for (const m of markers) {
        const r = getMarkerRadiusImagePx(m);
        if (r < 4) continue;
        if (Math.hypot(pt.x - m.x, pt.y - m.y) < r * 0.45) return true;
    }
    return false;
};

/**
 * True when the face's own interior (a point guaranteed inside the polygon) lands inside
 * a round bin's circle — i.e. the face genuinely occupies bin-interior space, not just a
 * label that happens to overlap. Legit pockets/gaps/wedges sit fully outside bin circles,
 * so their interior point is never inside a bin. Applies even to grazing pockets.
 */
const faceInteriorInsideRoundBin = (face, markers) => {
    if (!face?.polygon?.length || !markers?.length) return false;
    const pt = face.labelPoint || face.centroid || polygonCentroid(face.polygon);
    if (!pointInPolygon(pt.x, pt.y, face.polygon)) return false;
    for (const m of markers) {
        const r = getMarkerRadiusImagePx(m);
        if (r < 4) continue;
        if (Math.hypot(pt.x - m.x, pt.y - m.y) < r * 0.85) return true;
    }
    return false;
};

/**
 * Stice faces must not cover round-bin interiors (bins are separate assignable areas).
 * Channel pockets (flat tool, wall pair, synthesized channel) graze bin perimeters by design.
 */
const faceOverlapsRoundBin = (face, markers, segments, userSegmentIds) => {
    if (!markers?.length || !face?.polygon?.length) return false;
    if (faceCoreInsideRoundBinMarker(face, markers)) return true;
    if (faceInteriorInsideRoundBin(face, markers)) return true;
    const flatTool = isFlatToolFace(face, segments);
    const synthesized = faceIdIsSynthesizedPocket(face);
    const grazed = synthesized
        ? grazedBinIdsForSynthesizedFace(face, segments, markers)
        : new Set();
    for (const m of markers) {
        if (pointInPolygon(m.x, m.y, face.polygon)) return true;
        if (flatTool) continue;
        if (synthesized && grazed.has(m.id)) continue;
        if (polygonIntrudesBinDisk(face.polygon, m, 1)) return true;
    }
    return false;
};

const polygonBBox = (poly) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    poly.forEach((p) => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    return { minX, minY, maxX, maxY };
};

/** Lightweight face for React state — full polygons stay in a ref (avoids profiler OOM). */
export const enclosedFaceSummaryForState = (face) => {
    const bb =
        face.polygon?.length > 0
            ? polygonBBox(face.polygon)
            : face.bbox ?? null;
    return {
        id: face.id,
        area: face.area,
        centroid: face.centroid,
        labelPoint: face.labelPoint,
        segmentIds: face.segmentIds,
        binArcKeys: face.binArcKeys,
        bbox: bb,
    };
};

const bboxOverlapFraction = (a, b) => {
    const ba = polygonBBox(a.polygon);
    const bb = polygonBBox(b.polygon);
    const ix = Math.max(
        0,
        Math.min(ba.maxX, bb.maxX) - Math.max(ba.minX, bb.minX)
    );
    const iy = Math.max(
        0,
        Math.min(ba.maxY, bb.maxY) - Math.max(ba.minY, bb.minY)
    );
    const inter = ix * iy;
    const areaA = Math.max(1, (ba.maxX - ba.minX) * (ba.maxY - ba.minY));
    const areaB = Math.max(1, (bb.maxX - bb.minX) * (bb.maxY - bb.minY));
    return inter / Math.min(areaA, areaB);
};

/** True if two faces overlap in a meaningful way (centroid inside the other or heavy polygon overlap). */
const facesOverlap = (a, b) => {
    if (!a?.polygon?.length || !b?.polygon?.length) return false;
    if (a.id === b.id) return false;
    const ca = a.centroid || polygonCentroid(a.polygon);
    const cb = b.centroid || polygonCentroid(b.polygon);
    if (pointInPolygon(ca.x, ca.y, b.polygon)) return true;
    if (pointInPolygon(cb.x, cb.y, a.polygon)) return true;
    const la = a.labelPoint || ca;
    const lb = b.labelPoint || cb;
    if (pointInPolygon(la.x, la.y, b.polygon) && pointInPolygon(lb.x, lb.y, a.polygon)) {
        return true;
    }
    return false;
};

/**
 * Graph face lies inside a synthesized bin-hub pocket. `facesOverlap` alone misses split
 * halves of a 2×2 void (M/N, W/X) when only the label point sits inside the full pocket.
 */
const faceCoveredByBinHubSynth = (face, synth) => {
    if (!face?.polygon?.length || !synth?.polygon?.length) return false;
    if (facesOverlap(face, synth)) return true;
    const probes = [
        face.labelPoint,
        face.centroid,
        face.polygon?.length ? polygonCentroid(face.polygon) : null,
    ].filter((p) => p && Number.isFinite(p.x));
    return probes.some((p) => pointInPolygon(p.x, p.y, synth.polygon));
};

/** Drop planar-graph fills replaced by curved bin-perimeter hub pockets. */
const replaceGraphFacesWithBinHubSynth = (
    faces,
    synthList,
    markers,
    segments,
    userSegmentIds
) => {
    if (!synthList?.length) return faces;
    let out = faces.filter((f) => {
        if (faceIdIsSynthesizedPocket(f)) return true;
        return !synthList.some((t) => faceCoveredByBinHubSynth(f, t));
    });
    synthList.forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (!out.some((f) => f.id === sf.id)) out.push(sf);
    });
    return out;
};

/**
 * One enclosed pocket per 2×2 bin hub. Planar graph chords and `gap:tri:*` pockets
 * often bisect the void (M/N, W/X); keep a single `grid:bin:*` when walls do not split it.
 */
const consolidate2x2BinHubPockets = (
    faces,
    markers,
    segments,
    userSegmentIds
) => {
    let result = faces;
    find2x2BinHubGrids(markers).forEach((grid) => {
        const { tl, tr, bl, br } = grid;
        const hub = {
            x: (tl.x + tr.x + bl.x + br.x) / 4,
            y: (tl.y + tr.y + bl.y + br.y) / 4,
        };
        if (has2x2InteriorSplit(tl, tr, bl, br, segments, markers, hub)) return;

        const involved = [tl.id, tr.id, bl.id, br.id];
        const key = involved.sort().join(",");
        const wallId = `grid:wall:${key}`;
        const wallCandidate = result.find((f) => f.id === wallId);
        if (wallCandidate) {
            const segGeom = (segId) => {
                const raw = (segments || []).find((s) => s.id === segId);
                return raw
                    ? resolveSegmentGeometry(raw, markers, segments)
                    : null;
            };
            const topGeom = segGeom(wallCandidate.segmentIds?.[0]);
            const botGeom = segGeom(wallCandidate.segmentIds?.[1]);
            if (
                !horizontalWallPairCapsInteriorHubVoid(
                    tl,
                    tr,
                    bl,
                    br,
                    topGeom,
                    botGeom
                )
            ) {
                result = result.filter((f) => f.id !== wallId);
            }
        }

        const poly = build2x2BinCentralPocketPolygon(tl, tr, bl, br);
        if (!poly?.length) return;
        if (!polygonValidFor2x2BinHubPocket(poly, markers, involved)) return;
        if (!pointInPolygon(hub.x, hub.y, poly)) return;

        const makeGridBinFace = () => {
            const built = build2x2BinCentralPocketFace(
                tl,
                tr,
                bl,
                br,
                markers,
                segments,
                userSegmentIds
            );
            if (built) return built;
            if (faceOverlapsRoundBin(
                {
                    id: `grid:bin:${key}`,
                    polygon: poly,
                    segmentIds: [],
                    binArcKeys: [],
                },
                markers,
                segments,
                userSegmentIds
            )) {
                return null;
            }
            return {
                id: `grid:bin:${key}`,
                polygon: poly,
                centroid: polygonCentroid(poly),
                labelPoint: polygonLabelPoint(poly, markers),
                area: Math.abs(polygonArea(poly)),
                segmentIds: [],
                binArcKeys: [],
            };
        };

        const hubRef =
            makeGridBinFace() || {
                id: `hubref:${involved.sort().join(",")}`,
                polygon: poly,
                centroid: polygonCentroid(poly),
                labelPoint: polygonLabelPoint(poly, markers),
                area: Math.abs(polygonArea(poly)),
            };

        const labelPointInHub = (f) => {
            const pt =
                f.labelPoint ||
                f.centroid ||
                (f.polygon?.length ? polygonCentroid(f.polygon) : null);
            return pt && pointInPolygon(pt.x, pt.y, poly);
        };

        const facesInHub = result.filter(
            (f) => labelPointInHub(f) || faceCoveredByBinHubSynth(f, hubRef)
        );
        const binFaceForHub =
            result.find((f) => f.id === `grid:bin:${key}`) || makeGridBinFace();
        if (facesInHub.length <= 1) {
            if (binFaceForHub) {
                const sole = facesInHub[0];
                if (!sole || sole.id !== binFaceForHub.id) {
                    if (sole) {
                        result = result.filter((f) => f.id !== sole.id);
                    }
                    if (!result.some((f) => f.id === binFaceForHub.id)) {
                        result.push(binFaceForHub);
                    }
                }
            }
            return;
        }

        const hasWallSplitPocket = facesInHub.some(
            (f) =>
                String(f.id).startsWith("wallquad:") ||
                String(f.id).startsWith("wallstrip:")
        );
        if (hasWallSplitPocket) return;

        let wallFace = result.find((f) => f.id === `grid:wall:${key}`);
        const binFace = binFaceForHub;
        let keeper = null;
        if (wallFace && binFace) {
            const wallArea =
                wallFace.area ??
                Math.abs(polygonArea(wallFace.polygon || []));
            const binArea =
                binFace.area ??
                Math.abs(polygonArea(binFace.polygon || []));
            keeper = wallArea >= binArea * 0.72 ? wallFace : binFace;
        } else {
            keeper =
                binFace ||
                wallFace ||
                (hubRef.id.startsWith("grid:bin:") || hubRef.id.startsWith("grid:wall:")
                    ? hubRef
                    : null) ||
                facesInHub.find((f) => f.id === `gap:hub:${key}`);
        }

        if (!keeper) return;

        const dropIds = new Set(
            facesInHub.filter((f) => f.id !== keeper.id).map((f) => f.id)
        );
        result = result.filter((f) => !dropIds.has(f.id));
        if (!result.some((f) => f.id === keeper.id)) result.push(keeper);
    });
    return result;
};

/** Drop larger faces that swallow a smaller face's centroid (stacked duplicate flats). */
const dropFacesSwallowingSmaller = (faces) =>
    faces.filter((f) => {
        if (
            String(f.id).startsWith("channel:") ||
            String(f.id).startsWith("wallloop:") ||
            String(f.id).startsWith("wallquad:") ||
            String(f.id).startsWith("grid:") ||
            String(f.id).startsWith("corner:")
        ) {
            return true;
        }
        const fArea = f.area ?? Math.abs(polygonArea(f.polygon));
        const fc = f.centroid || polygonCentroid(f.polygon);
        return !faces.some((g) => {
            if (g.id === f.id) return false;
            const gArea = g.area ?? Math.abs(polygonArea(g.polygon));
            if (fArea <= gArea) return false;
            const gc = g.centroid || polygonCentroid(g.polygon);
            return pointInPolygon(gc.x, gc.y, f.polygon);
        });
    });

/** Small graph face lying inside a larger synthesized arc pocket (spurious interior sliver). */
const graphFaceInsideArcSynth = (face, synthArc) => {
    if (String(face.id).startsWith("arc:")) return false;
    const cent = face.centroid || polygonCentroid(face.polygon);
    const faceArea = face.area ?? Math.abs(polygonArea(face.polygon));
    return synthArc.some((s) => {
        const synthArea = s.area ?? Math.abs(polygonArea(s.polygon));
        if (faceArea >= synthArea * 0.45) return false;
        return pointInPolygon(cent.x, cent.y, s.polygon);
    });
};

/**
 * Drop overlaps; synthesized arc pockets (`arc:*`) always win over graph slivers.
 */
/**
 * Planar-graph fills over many bins (fan into bin interiors) — not a layout-table stice.
 * Synthesized pockets (arc:/grid:/corner:) are never spurious.
 */
const faceIdIsSynthesizedPocket = (face) => {
    const id = String(face?.id ?? "");
    return (
        id.startsWith("arc:") ||
        id.startsWith("grid:") ||
        id.startsWith("gap:tri:") ||
        id.startsWith("gap:hub:") ||
        id.startsWith("gap:throat:") ||
        id.startsWith("wallwedge:") ||
        id.startsWith("wallstrip:") ||
        id.startsWith("wallloop:") ||
        id.startsWith("wallquad:") ||
        id.startsWith("corner:") ||
        id.startsWith("flat:") ||
        id.startsWith("channel:") ||
        id.startsWith("outer:")
    );
};

/** Bins whose perimeter the synthesized pocket is allowed to graze (not interior-fill). */
const grazedBinIdsForSynthesizedFace = (face, segments, markers) => {
    const id = String(face?.id ?? "");
    if (id.startsWith("grid:bin:")) return new Set(id.slice(9).split(","));
    if (id.startsWith("gap:tri:")) return new Set(id.slice(8).split(","));
    if (id.startsWith("gap:hub:")) return new Set(id.slice(8).split(","));
    if (id.startsWith("gap:throat:")) {
        const body = id.slice(11);
        const [hubId, pair] = body.split("|");
        return new Set([hubId, ...(pair ? pair.split(",") : [])].filter(Boolean));
    }
    if (id.startsWith("wallwedge:")) {
        const parts = id.split(":");
        return new Set(parts.slice(-2).filter(Boolean));
    }
    if (id.startsWith("wallstrip:")) {
        const parts = id.split(":");
        const pair = parts[2] || "";
        return new Set(pair.split(",").filter(Boolean));
    }
    if (id.startsWith("wallloop:")) {
        const parts = id.split(":");
        const bins = parts[2] || "";
        return new Set(bins.split(",").filter(Boolean));
    }
    if (id.startsWith("wallquad:")) {
        const parts = id.split(":");
        const bins = parts[1] || "";
        return new Set(bins.split(",").filter(Boolean));
    }
    if (id.startsWith("grid:")) {
        const parts = id.slice(5).split(":");
        return new Set(parts.filter((p) => markers?.some((m) => m.id === p)));
    }
    if (id.startsWith("corner:")) {
        const binId = id.split(":")[1];
        return binId ? new Set([binId]) : new Set();
    }
    if (id.startsWith("outer:")) {
        const binId = id.split(":")[1];
        return binId ? new Set([binId]) : new Set();
    }
    if (id.startsWith("arc:") && face.segmentIds?.[0] && segments?.length) {
        const seg = segments.find((s) => s.id === face.segmentIds[0]);
        const geom = seg ? resolveSegmentGeometry(seg, markers, segments) : null;
        if (geom) {
            return new Set([geom.markerId1, geom.markerId2].filter(Boolean));
        }
    }
    return new Set();
};

export const faceIsSpuriousBinArcGraphFill = (face, markers, feetPerPixel) => {
    if (faceIdIsSynthesizedPocket(face)) return false;
    if (faceIsLegitimateMultiBinGap(face, markers)) return false;
    if ((face.segmentIds || []).length > 0) return false;
    const binArcs = face.binArcKeys?.length ?? 0;
    if (binArcs < 2) return false;
    if (
        face.polygon?.length >= 3 &&
        markers?.some((m) => pointInPolygon(m.x, m.y, face.polygon))
    ) {
        return true;
    }
    const c = face.centroid;
    if (
        c &&
        markers?.some(
            (m) =>
                Math.hypot(c.x - m.x, c.y - m.y) <
                getMarkerRadiusImagePx(m) * 0.85
        )
    ) {
        return true;
    }
    return false;
};

/** Only faces that map to real layout-table stices (not planar graph junk). */
export const faceEligibleForLayoutTableStice = (face, markers, feetPerPixel) => {
    if (faceIsSpuriousBinArcGraphFill(face, markers, feetPerPixel)) return false;
    if (faceIdIsSynthesizedPocket(face)) return true;
    if ((face.segmentIds || []).length > 0) return true;
    const binArcs = face.binArcKeys?.length ?? 0;
    if (binArcs < 2) return false;
    const fpp = feetPerPixel > 0 ? feetPerPixel : PIXEL_TO_FEET_DEFAULT;
    const areaFt2 = (face.area ?? 0) * fpp * fpp;
    return areaFt2 > 40 && areaFt2 < 700 && binArcs <= 4;
};

/**
 * Layout origin in image px — same reference for 2D viewport pan and draft feet (buildDraft).
 * Uses marker centers and enclosed-area polygons (not blank-canvas center).
 */
export const computeLayoutSiteCenterPx = (markers, faces = [], imageFallback = null) => {
    const pts = [];
    (markers || []).forEach((m) => {
        if (Number.isFinite(m.x) && Number.isFinite(m.y)) {
            pts.push({ x: m.x, y: m.y });
        }
    });
    (faces || []).forEach((f) => {
        if (f.polygon?.length) {
            f.polygon.forEach((p) => {
                if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
                    pts.push({ x: p.x, y: p.y });
                }
            });
        } else if (f.bbox) {
            pts.push(
                { x: f.bbox.minX, y: f.bbox.minY },
                { x: f.bbox.maxX, y: f.bbox.maxY }
            );
        }
    });
    if (!pts.length) {
        const w = imageFallback?.width;
        const h = imageFallback?.height;
        if (w > 0 && h > 0) {
            return { x: w / 2, y: h / 2 };
        }
        return null;
    }
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    pts.forEach((p) => {
        minX = Math.min(minX, p.x);
        maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y);
        maxY = Math.max(maxY, p.y);
    });
    return {
        x: (minX + maxX) / 2,
        y: (minY + maxY) / 2,
    };
};

/**
 * Whether a face becomes a flat CustomerSiteLayoutBin row (Site 3D SticeArcWall + layoutSides).
 * Bin-arc pockets between round silos must persist — round bins alone do not render interstitial stices.
 */
export const faceShouldPersistAsLayoutSticeBin = (
    face,
    markers,
    feetPerPixel,
    options = {}
) => {
    if (faceIsSpuriousBinArcGraphFill(face, markers, feetPerPixel)) {
        return false;
    }
    if (options.forSave === true) {
        return faceEligibleForLayoutDraft(face, markers, feetPerPixel, {
            forSave: true,
        });
    }
    return faceEligibleForLayoutDraft(face, markers, feetPerPixel, options);
};

/**
 * Hub/wall synthesized pockets use face.polygon only (no segment loop / binArcKeys).
 * Site 3D and Preview must accept these the same as gap:tri / gap:hub pockets.
 */
export const faceHasSynthOutlineForDraft = (face) => {
    if ((face?.polygon?.length ?? 0) < 3) return false;
    const id = String(face?.id ?? "");
    return (
        id.startsWith("gap:tri:") ||
        id.startsWith("gap:hub:") ||
        id.startsWith("gap:throat:") ||
        id.startsWith("grid:bin:") ||
        id.startsWith("grid:wall:")
    );
};

/** Preview/save draft: include every pocket the 2D editor draws (relaxed area rules). */
export const faceEligibleForLayoutDraft = (
    face,
    markers,
    feetPerPixel,
    options = {}
) => {
    const relaxed = options.forPreview === true || options.forSave === true;
    if (faceIsSpuriousBinArcGraphFill(face, markers, feetPerPixel)) return false;
    if (relaxed) {
        if (faceIdIsSynthesizedPocket(face)) return true;
        if ((face.segmentIds || []).length > 0) return true;
        return (face.binArcKeys?.length ?? 0) >= 2;
    }
    return faceEligibleForLayoutTableStice(face, markers, feetPerPixel);
};

/**
 * Planar-graph pockets beside a user arc (wall + bin arcs) use straight chords;
 * drop them when a synthesized `arc:*` pocket covers the same region.
 */
const dropGraphArcChannelFills = (faces, arcSynth, userSegmentIds, resolvedById) => {
    const synthArc = arcSynth.filter((sf) => String(sf.id).startsWith("arc:"));
    if (!synthArc.length) return faces;

    const synthArcSegIds = new Set(
        synthArc.map((sf) => String(sf.id).slice(4))
    );
    const synthPairs = [];
    synthArc.forEach((sf) => {
        const g = resolvedById.get(String(sf.id).slice(4));
        if (g) synthPairs.push(new Set([g.markerId1, g.markerId2].filter(Boolean)));
    });

    return faces.filter((f) => {
        if (String(f.id).startsWith("arc:")) return true;
        if (isMultiBinCentralGraphPocket(f, userSegmentIds)) return true;

        const userSegs = (f.segmentIds || []).filter((id) => userSegmentIds.has(id));
        if (userSegs.some((id) => synthArcSegIds.has(id))) return false;

        if (
            synthArc.some((s) => {
                if (!facesOverlap(f, s) && !graphFaceInsideArcSynth(f, [s])) {
                    return false;
                }
                if (isMultiBinCentralGraphPocket(f, userSegmentIds)) return false;
                if (graphFaceUserWallCount(f, userSegmentIds, resolvedById) >= 2) {
                    return false;
                }
                return true;
            })
        ) {
            return false;
        }

        const mids = markerIdsFromBinArcKeys(f.binArcKeys);
        if (mids.size < 2) return true;

        for (const pair of synthPairs) {
            if (pair.size < 2) continue;
            const exactPair =
                mids.size === pair.size &&
                [...pair].every((id) => mids.has(id));
            if (!exactPair) continue;
            if (userSegs.length === 1) return false;
        }
        return true;
    });
};

const dedupeOverlappingFaces = (faces, userSegmentIds) => {
    const synthArc = faces.filter((f) => String(f.id).startsWith("arc:"));
    const other = faces.filter((f) => !String(f.id).startsWith("arc:"));
    const keptSynth = [];
    synthArc.forEach((f) => {
        if (keptSynth.some((k) => facesOverlap(f, k))) return;
        keptSynth.push(f);
    });
    const keptOther = [];
    [...other]
        .sort((a, b) => a.area - b.area)
        .forEach((f) => {
            const userSegCount = (f.segmentIds || []).filter((id) =>
                userSegmentIds.has(id)
            ).length;
            if (
                keptSynth.some((s) => facesOverlap(f, s)) &&
                !isMultiBinCentralGraphPocket(f, userSegmentIds) &&
                userSegCount < 2
            ) {
                return;
            }
            if (keptOther.some((k) => facesOverlap(f, k))) return;
            keptOther.push(f);
        });
    return [...keptOther, ...keptSynth];
};

/** Graph face wholly on the exterior (bulge) side of a user arc — planar sliver, not a real stice. */
const graphFaceOnArcBulgeSide = (face, resolvedById) => {
    if (
        String(face.id).startsWith("arc:") ||
        String(face.id).startsWith("grid:") ||
        String(face.id).startsWith("corner:")
    ) {
        return false;
    }
    const userSegCount = (face.segmentIds || []).length;
    const binArcCount = face.binArcKeys?.length ?? 0;
    if (userSegCount >= 2 || binArcCount >= 4 || (userSegCount === 1 && binArcCount >= 2)) {
        return false;
    }
    const cent = face.centroid || polygonCentroid(face.polygon);
    for (const geom of resolvedById.values()) {
        if (geom.kind !== "arc" || geom.curveX == null) continue;
        const bulgeSide = bulgeSideOfChord(geom, geom.curveX, geom.curveY);
        if (Math.abs(bulgeSide) < 1e-3) continue;
        if (Math.sign(bulgeSideOfChord(geom, cent.x, cent.y)) !== Math.sign(bulgeSide)) {
            continue;
        }
        const chordLen = Math.hypot(geom.x2 - geom.x1, geom.y2 - geom.y1);
        if (chordLen < 1e-3) continue;
        const dx = geom.x2 - geom.x1;
        const dy = geom.y2 - geom.y1;
        const t = ((cent.x - geom.x1) * dx + (cent.y - geom.y1) * dy) / (chordLen * chordLen);
        if (t < -0.15 || t > 1.15) continue;
        return true;
    }
    return false;
};

const arcPocketAcceptsPolygon = (poly, geom, markers, involved) => {
    if (!polygonValidForArcPocket(poly, markers, involved)) return false;
    if (arcPocketIntrudesInvolvedBinInterior(poly, geom, markers, involved)) return false;
    const testPt = arcInteriorTestPoint(geom);
    if (pointInPolygon(testPt.x, testPt.y, poly)) return true;
    if (pointInPolygon(geom.curveX, geom.curveY, poly)) return true;
    return false;
};

/** Wall + arc pockets: accept when arc bulge or wall midpoint lies inside the polygon. */
const wallArcPocketAcceptsPolygon = (poly, geom, markers, involved, wallGeom) => {
    if (!polygonValidForArcPocket(poly, markers, involved)) return false;
    if (arcPocketIntrudesInvolvedBinInterior(poly, geom, markers, involved)) return false;
    const testPt = arcInteriorTestPoint(geom);
    if (pointInPolygon(testPt.x, testPt.y, poly)) return true;
    if (pointInPolygon(geom.curveX, geom.curveY, poly)) return true;
    const wmx = (wallGeom.x1 + wallGeom.x2) / 2;
    const wmy = (wallGeom.y1 + wallGeom.y2) / 2;
    return pointInPolygon(wmx, wmy, poly);
};

/**
 * Graph faces from a single wall/arc before the layout closes are usually phantom fills.
 * Accept:
 *   - pure bin stices: no user edges + ≥2 bin perimeter arcs (4-bin diamonds)
 *   - multi-segment loops: ≥2 walls/arcs (any bin arc count)
 *   - wall+bin pockets: exactly 1 user wall + ≥2 bin arcs (around-G corner pockets)
 *     — requires no marker center inside the polygon and area above a phantom threshold.
 */
const graphFaceIsWellBounded = (face, userSegmentIds, markers) => {
    if (faceIdIsSynthesizedPocket(face)) return true;
    const userSegCount = (face.segmentIds || []).filter((id) => userSegmentIds.has(id)).length;
    const binArcCount = face.binArcKeys?.length ?? 0;
    if (userSegCount === 0) {
        if (binArcCount < 2) return false;
        return !markers?.some((m) =>
            polygonIntrudesBinDisk(face.polygon, m, 1)
        );
    }
    if (userSegCount >= 2) {
        return !markers?.some((m) =>
            polygonIntrudesBinDisk(face.polygon, m, 1)
        );
    }
    if (userSegCount === 1 && binArcCount >= 1) {
        if (!face.polygon?.length) return false;
        if (faceCoreInsideRoundBinMarker(face, markers)) return false;
        if ((face.area ?? Math.abs(polygonArea(face.polygon))) < 80) return false;
        if (markers?.some((m) => pointInPolygon(m.x, m.y, face.polygon))) return false;
        return !markers?.some((m) => polygonIntrudesBinDisk(face.polygon, m, 1));
    }
    return false;
};

const dedupeAngles = (angles) => {
    const sorted = [...angles].map(normalizeAngle).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((a) => {
        if (!out.length || Math.abs(a - out[out.length - 1]) > 0.05) out.push(a);
    });
    if (out.length >= 2 && 2 * Math.PI - (out[out.length - 1] - out[0]) < 0.05) {
        out.pop();
    }
    return out;
};

/** Rectangle flat-tool houses as assignable faces (no round bins required). */
const synthesizeClosedRectFaces = (markers, segments) => {
    const byRect = new Map();
    (segments || []).forEach((seg) => {
        if (!seg.closedRectId) return;
        if (isFloatingSegment(seg, markers, segments)) return;
        if (!byRect.has(seg.closedRectId)) byRect.set(seg.closedRectId, []);
        byRect.get(seg.closedRectId).push(seg);
    });
    const faces = [];
    byRect.forEach((segs, rectId) => {
        if (segs.length < 4) return;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        segs.forEach((s) => {
            const g = resolveSegmentGeometry(s, markers, segments);
            if (!g) return;
            minX = Math.min(minX, g.x1, g.x2);
            minY = Math.min(minY, g.y1, g.y2);
            maxX = Math.max(maxX, g.x1, g.x2);
            maxY = Math.max(maxY, g.y1, g.y2);
        });
        if (!Number.isFinite(minX)) return;
        const polygon = [
            { x: minX, y: minY },
            { x: maxX, y: minY },
            { x: maxX, y: maxY },
            { x: minX, y: maxY },
        ];
        const area = Math.abs(polygonArea(polygon));
        if (area < 80) return;
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        faces.push({
            id: `flat:${rectId}`,
            polygon,
            centroid: { x: cx, y: cy },
            labelPoint: { x: cx, y: cy },
            area,
            segmentIds: segs.map((s) => s.id),
            binArcKeys: [],
        });
    });
    return faces;
};

const dropExteriorFaceWithoutMarkers = (faces) => {
    if (faces.length <= 1) return faces;
    const sorted = faces.slice().sort((a, b) => (b.area ?? 0) - (a.area ?? 0));
    const largest = sorted[0];
    const rest = sorted.slice(1);
    const exterior =
        rest.length > 0 &&
        rest.every((f) => {
            const c = f.centroid || polygonCentroid(f.polygon);
            return pointInPolygon(c.x, c.y, largest.polygon);
        });
    return exterior ? rest : faces;
};

const buildResolvedSegmentMap = (markers, segments) => {
    const resolvedById = new Map();
    const userSegmentIds = new Set();
    (segments || []).forEach((seg) => {
        if (isFloatingSegment(seg, markers, segments)) return;
        const geom = resolveSegmentGeometry(seg, markers, segments);
        if (!geom || !seg.id) return;
        userSegmentIds.add(seg.id);
        resolvedById.set(seg.id, geom);
    });
    return { resolvedById, userSegmentIds };
};

/** Large sites: arc/grid synthesis only (no O(n²) planar face walk). */
const buildFastEnclosedFaces = (markers, segments) => {
    const { resolvedById, userSegmentIds } = buildResolvedSegmentMap(
        markers,
        segments
    );
    const seenIds = new Set();
    let filtered = [];
    const push = (sf) => {
        if (!sf || seenIds.has(sf.id)) return;
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (
            faceCoreInsideRoundBinMarker(sf, markers) &&
            !faceIsLegitimateMultiBinGap(sf, markers)
        ) {
            return;
        }
        seenIds.add(sf.id);
        filtered.push(sf);
    };

    synthesizeArcPocketFaces(markers, resolvedById, segments).forEach(push);
    synthesizeWallBinWedgePockets(markers, resolvedById, segments).forEach(push);
    const interiorWallSynth = synthesize2x2InteriorWallRegions(
        markers,
        resolvedById,
        segments
    );
    const fastWallQuads = interiorWallSynth.filter((f) =>
        String(f.id).startsWith("wallquad:")
    );
    if (fastWallQuads.length) {
        filtered = dropFacesObscuring2x2WallQuads(filtered, fastWallQuads);
    }
    interiorWallSynth.forEach(push);
    synthesizeJunctionClosedWallRects(markers, resolvedById).forEach(push);
    synthesizeWallChannelPockets(markers, resolvedById, segments).forEach(push);
    synthesize2x2BinCentralPockets(markers, segments).forEach(push);
    synthesizeTripleBinGapPockets(markers, segments).forEach(push);
    synthesizeHubThroatPockets(markers, resolvedById).forEach(push);
    synthesize2x2ArcCentralPockets(markers, resolvedById, segments).forEach(push);
    synthesize2x2WallRowCentralPockets(markers, segments).forEach(push);
    synthesizeChannelQuadCornerPockets(markers, resolvedById).forEach(push);
    synthesizePerimeterCornerPockets(markers, resolvedById).forEach(push);

    filtered.forEach((f) => {
        if (!f.labelPoint) f.labelPoint = polygonLabelPoint(f.polygon, markers);
    });
    filtered = dedupeOverlappingFaces(filtered, userSegmentIds);
    filtered = consolidate2x2BinHubPockets(
        filtered,
        markers,
        segments,
        userSegmentIds
    );

    if (!markers?.length) {
        return dropExteriorFaceWithoutMarkers(filtered);
    }
    if (filtered.length <= 1) return filtered;
    return filtered.filter((f) => {
        let inside = 0;
        markers.forEach((m) => {
            if (pointInPolygon(m.x, m.y, f.polygon)) inside += 1;
        });
        return inside < Math.max(2, markers.length - 1);
    });
};

/**
 * Build planar graph and return enclosed faces (image px).
 */
export const computeEnclosedFaces = (markers, segmentsIn) => {
    const segments = Array.isArray(segmentsIn) ? segmentsIn : [];
    const cacheKey = buildLayoutGeometryCacheKey(markers, segments);
    if (cacheKey === enclosureCacheKey) {
        return enclosureCacheResult;
    }

    const commitEnclosureResult = (faces) => {
        enclosureCacheKey = cacheKey;
        enclosureCacheResult = faces;
        return faces;
    };

    const hasGeometry = segments.some(
        (seg) =>
            !isFloatingSegment(seg, markers, segments) &&
            resolveSegmentGeometry(seg, markers, segments)
    );
    if (!markers?.length && !hasGeometry) return commitEnclosureResult([]);

    if (shouldUseFastEnclosedFaces(markers, segments)) {
        return commitEnclosureResult(
            buildFastEnclosedFaces(markers, segments)
        );
    }

    const vertices = new Map();
    const ensureVertex = (id, x, y, extra = {}) => {
        if (!vertices.has(id)) vertices.set(id, { id, x, y, ...extra });
        return id;
    };

    const registerBinAngle = (markerId, angle) => {
        if (markerId == null || angle == null) return;
        if (!markerAngles.has(markerId)) markerAngles.set(markerId, new Set());
        markerAngles.get(markerId).add(normalizeAngle(angle));
    };

    /**
     * Resolve a graph vertex for an incoming geometry point. When the point sits on a
     * bin's perimeter (known markerId+angle, or inferred via a near-perimeter coord),
     * the vertex coord is normalised to the exact bin perimeter point so the wall
     * endpoint and the bin's perimeter sub-arc end at the same `coordKey`.
     * Returns { id, x, y, markerId, angle }.
     */
    const resolveGraphVertex = (markerId, angle, x, y) => {
        let resolvedMarkerId = markerId ?? null;
        let resolvedAngle = angle != null ? normalizeAngle(angle) : null;

        if (resolvedMarkerId == null || resolvedAngle == null) {
            const inferred = inferBinSnap(x, y, markers);
            if (inferred) {
                if (resolvedMarkerId == null) resolvedMarkerId = inferred.markerId;
                if (resolvedAngle == null) resolvedAngle = inferred.angle;
                registerBinAngle(inferred.markerId, inferred.angle);
            }
        }

        if (resolvedMarkerId != null && resolvedAngle != null) {
            const marker = markers.find((m) => m.id === resolvedMarkerId);
            if (marker) {
                const r = getMarkerRadiusImagePx(marker);
                const px = marker.x + r * Math.cos(resolvedAngle);
                const py = marker.y + r * Math.sin(resolvedAngle);
                return {
                    id: coordKey(px, py),
                    x: px,
                    y: py,
                    markerId: resolvedMarkerId,
                    angle: resolvedAngle,
                };
            }
        }

        return {
            id: coordKey(x, y),
            x,
            y,
            markerId: resolvedMarkerId,
            angle: resolvedAngle,
        };
    };

    const edges = [];
    const addEdge = (v1, v2, meta) => {
        if (v1 === v2) return;
        edges.push({ v1, v2, ...meta });
    };

    const addPolyline = (pointIds, meta) => {
        for (let i = 0; i < pointIds.length - 1; i++) {
            addEdge(pointIds[i], pointIds[i + 1], meta);
        }
    };

    const markerAngles = new Map();
    registerTouchingBinAngles(markers, registerBinAngle);
    const userSegmentIds = new Set();
    const resolvedById = new Map();
    const junctionsBySegment = new Map();

    const addJunction = (segId, x, y) => {
        if (x == null || y == null) return;
        if (!junctionsBySegment.has(segId)) junctionsBySegment.set(segId, []);
        const list = junctionsBySegment.get(segId);
        if (!list.some((p) => Math.hypot(p.x - x, p.y - y) <= JUNCTION_TOL_PX)) {
            list.push({ x, y });
        }
    };

    segments.forEach((seg) => {
        if (isFloatingSegment(seg, markers, segments)) return;
        const geom = resolveSegmentGeometry(seg, markers, segments);
        if (!geom) return;
        if (geom.kind === "arc") {
            if (geom.markerId1 == null || geom.angle1 == null) {
                const inf = inferBinSnap(geom.x1, geom.y1, markers);
                if (inf) {
                    geom.markerId1 = inf.markerId;
                    geom.angle1 = inf.angle;
                }
            }
            if (geom.markerId2 == null || geom.angle2 == null) {
                const inf = inferBinSnap(geom.x2, geom.y2, markers);
                if (inf) {
                    geom.markerId2 = inf.markerId;
                    geom.angle2 = inf.angle;
                }
            }
            if (
                geom.markerId1 == null ||
                geom.markerId2 == null ||
                geom.angle1 == null ||
                geom.angle2 == null
            ) {
                if (geom.curveX == null || geom.curveY == null) return;
            }
        }
        userSegmentIds.add(seg.id);
        resolvedById.set(seg.id, geom);

        [1, 2].forEach((end) => {
            const attach = seg[`attach${end}`];
            if (attach?.segmentId && attach.role === "mid") {
                const ep = resolveEndpoint(seg, end, markers, segments);
                if (ep) addJunction(attach.segmentId, ep.x, ep.y);
            }
        });
    });

    resolvedById.forEach((geom, segId) => {
        if (geom.kind === "arc" && geom.curveX != null) {
            resolvedById.forEach((other, otherId) => {
                if (otherId === segId) return;
                [ { x: other.x1, y: other.y1 }, { x: other.x2, y: other.y2 } ].forEach((p) => {
                    if (distToUserArc(geom, p.x, p.y) <= JUNCTION_TOL_PX) {
                        addJunction(segId, p.x, p.y);
                    }
                });
            });
            return;
        }
        resolvedById.forEach((other, otherId) => {
            if (otherId === segId) return;
            [ { x: other.x1, y: other.y1 }, { x: other.x2, y: other.y2 } ].forEach((p) => {
                if (pointOnWallInterior(p.x, p.y, geom.x1, geom.y1, geom.x2, geom.y2)) {
                    addJunction(segId, p.x, p.y);
                }
            });
        });
    });

    /**
     * Split walls and bins where a wall runs tangent to / crosses a bin perimeter, so
     * regions bounded by a wall + bin arcs (e.g. an outer boundary rectangle around bins)
     * become closed faces. Without this the boundary and bins share no graph vertex.
     */
    resolvedById.forEach((geom, segId) => {
        if (geom.kind === "arc") return;
        markers.forEach((m) => {
            wallSegmentBinContacts(geom, m).forEach((pt) => {
                addJunction(segId, pt.x, pt.y);
                registerBinAngle(m.id, pt.angle);
            });
        });
    });

    registerArcNeighborAngles(markers, resolvedById, registerBinAngle);

    const registerGeomAngles = (geom) => {
        registerBinAngle(geom.markerId1, geom.angle1);
        registerBinAngle(geom.markerId2, geom.angle2);
        if (geom.markerId1 == null) {
            const inf = inferBinSnap(geom.x1, geom.y1, markers);
            if (inf) registerBinAngle(inf.markerId, inf.angle);
        }
        if (geom.markerId2 == null) {
            const inf = inferBinSnap(geom.x2, geom.y2, markers);
            if (inf) registerBinAngle(inf.markerId, inf.angle);
        }
    };

    const vertexForPoint = (p, geomHint, options = {}) => {
        const allowBinInfer = options.allowBinInfer !== false;
        const markerId =
            p.markerId ?? geomHint?.markerId ?? null;
        const angle =
            p.angle != null ? p.angle : null;
        const resolved = allowBinInfer
            ? resolveGraphVertex(markerId, angle, p.x, p.y)
            : {
                  id: coordKey(p.x, p.y),
                  x: p.x,
                  y: p.y,
                  markerId,
                  angle,
              };
        ensureVertex(resolved.id, resolved.x, resolved.y, {
            markerId: resolved.markerId,
            angle: resolved.angle,
        });
        if (resolved.markerId != null && resolved.angle != null) {
            registerBinAngle(resolved.markerId, resolved.angle);
        }
        return resolved.id;
    };

    resolvedById.forEach((geom, segId) => {
        const meta = { segmentId: segId, isUser: true };
        const junctions = junctionsBySegment.get(segId) || [];
        registerGeomAngles(geom);

        if (geom.kind === "arc" && geom.curveX != null) {
            const chain = arcPolylineWithJunctions(geom, junctions);
            const ids = chain.map((p) => vertexForPoint(p, geom, { allowBinInfer: true }));
            addPolyline(ids, meta);
            return;
        }

        const interior = junctions.filter((p) =>
            pointOnWallInterior(p.x, p.y, geom.x1, geom.y1, geom.x2, geom.y2)
        );
        const chain = sortPointsAlongWall(
            dedupeNearbyPoints(
                [
                    { x: geom.x1, y: geom.y1, markerId: geom.markerId1, angle: geom.angle1 },
                    ...interior,
                    { x: geom.x2, y: geom.y2, markerId: geom.markerId2, angle: geom.angle2 },
                ],
                JUNCTION_TOL_PX
            ),
            geom.x1,
            geom.y1,
            geom.x2,
            geom.y2
        );

        const ids = chain.map((p) => vertexForPoint(p, geom));
        if (ids.length < 2) return;
        if (ids.length === 2) addEdge(ids[0], ids[1], meta);
        else addPolyline(ids, meta);
    });

    /**
     * Subdivide each bin's perimeter at every registered angle (tangent contacts and
     * wall/arc attachments). Every consecutive sorted angle pair is one CCW perimeter
     * sub-arc so the full circle is tiled (required for bin-to-bin stices). With the
     * face walker correctly traverses every enclosed region and leaves the bin's interior
     * (filtered out below as a marker-overlap face) and the unbounded exterior to be
     * dropped by the marker-content filter.
     */
    markerAngles.forEach((angles, markerId) => {
        const marker = markers.find((m) => m.id === markerId);
        if (!marker) return;
        const sorted = dedupeAngles([...angles]).sort(
            (a, b) => normalizeAngle(a) - normalizeAngle(b)
        );
        if (sorted.length < 2) return;
        const r = getMarkerRadiusImagePx(marker);

        const ensureBinAngleVertex = (angle) => {
            const na = normalizeAngle(angle);
            const x = marker.x + r * Math.cos(na);
            const y = marker.y + r * Math.sin(na);
            const id = coordKey(x, y);
            ensureVertex(id, x, y, { markerId, angle: na });
            return id;
        };

        for (let i = 0; i < sorted.length; i++) {
            const a1 = sorted[i];
            const a2 = sorted[(i + 1) % sorted.length];
            const v1 = ensureBinAngleVertex(a1);
            const v2 = ensureBinAngleVertex(a2);
            const na1 = normalizeAngle(a1);
            const na2 = normalizeAngle(a2);
            let delta = na2 - na1;
            while (delta <= 0) delta += 2 * Math.PI;
            const steps = 10;
            const ids = [];
            for (let j = 0; j <= steps; j++) {
                if (j === 0) {
                    ids.push(v1);
                    continue;
                }
                if (j === steps) {
                    ids.push(v2);
                    continue;
                }
                const t = na1 + (delta * j) / steps;
                const px = marker.x + r * Math.cos(t);
                const py = marker.y + r * Math.sin(t);
                ids.push(ensureVertex(coordKey(px, py), px, py));
            }
            const binArcKey = `${markerId}:${na1.toFixed(4)}-${na2.toFixed(4)}`;
            addPolyline(ids, { binArcKey, isUser: false });
        }
    });

    if (edges.length < 3) {
        return commitEnclosureResult(
            synthesizeArcPocketFaces(markers, resolvedById, segments)
        );
    }

    const planarEdgeLimitExceeded = edges.length > MAX_PLANAR_EDGES;
    const faces = [];

    if (!planarEdgeLimitExceeded) {
    const adj = new Map();
    edges.forEach((e, edgeIdx) => {
        if (!adj.has(e.v1)) adj.set(e.v1, []);
        if (!adj.has(e.v2)) adj.set(e.v2, []);
        adj.get(e.v1).push({ to: e.v2, edgeIdx });
        adj.get(e.v2).push({ to: e.v1, edgeIdx });
    });

    adj.forEach((list, vid) => {
        const v = vertices.get(vid);
        list.sort((a, b) => {
            const va = vertices.get(a.to);
            const vb = vertices.get(b.to);
            return Math.atan2(va.y - v.y, va.x - v.x) - Math.atan2(vb.y - v.y, vb.x - v.x);
        });
    });

    const visitedDir = new Set();

    const nextHalfEdge = (at, from) => {
        const list = adj.get(at) || [];
        const fromIdx = list.findIndex((e) => e.to === from);
        if (fromIdx < 0 || list.length < 2) return null;
        return list[(fromIdx + 1) % list.length];
    };

    edges.forEach((e, edgeIdx) => {
        const dirs = [
            { cur: e.v1, prev: e.v2, edgeIdx },
            { cur: e.v2, prev: e.v1, edgeIdx },
        ];
        dirs.forEach(({ cur: startCur, prev: startPrev, edgeIdx: startEdgeIdx }) => {
            const startKey = `${startEdgeIdx}:${startCur}->${startPrev}`;
            if (visitedDir.has(startKey)) return;

            const poly = [];
            const segIds = new Set();
            const binKeys = new Set();
            let cur = startCur;
            let prev = startPrev;
            let incomingEdgeIdx = startEdgeIdx;
            let guard = 0;

            while (guard++ <= edges.length * 4) {
                const dirKey = `${incomingEdgeIdx}:${cur}->${prev}`;
                if (visitedDir.has(dirKey) && poly.length > 2) break;
                visitedDir.add(dirKey);

                const v = vertices.get(cur);
                poly.push({ x: v.x, y: v.y });

                const edge = edges[incomingEdgeIdx];
                if (edge?.segmentId) segIds.add(edge.segmentId);
                if (edge?.binArcKey) binKeys.add(edge.binArcKey);

                const step = nextHalfEdge(cur, prev);
                if (!step) break;
                prev = cur;
                cur = step.to;
                incomingEdgeIdx = step.edgeIdx;
                if (cur === startCur && prev === startPrev) break;
            }

            if (poly.length < 3) return;

            const area = polygonArea(poly);
            if (Math.abs(area) < 80) return;

            const hasUserEdge = [...segIds].some((id) => userSegmentIds.has(id));
            const hasBinLoop = binKeys.size >= 2;
            if (!hasUserEdge && !hasBinLoop) return;

            faces.push({
                id: faceSignature([...segIds], [...binKeys]),
                polygon: poly,
                centroid: polygonCentroid(poly),
                area: Math.abs(area),
                segmentIds: [...segIds],
                binArcKeys: [...binKeys],
            });
        });
    });
    }

    const arcProbes = arcInteriorProbesBySegmentId(resolvedById);
    const arcSegIds = new Set(arcProbes.keys());

    // A face is a true "arc wedge" only when its sole user boundary is one arc
    // (the rest being bin perimeter arcs). Such wedges are replaced by synthesized
    // arc pockets. Multi-segment faces that just happen to use an arc as one of
    // several boundaries (e.g., the central area between a 2x2 bin cluster with
    // arcs top/bottom and bins left/right) must be preserved.
    const faceIsArcWedgeCandidate = (f) => {
        const userSegs = (f.segmentIds || []).filter((id) => userSegmentIds.has(id));
        return userSegs.length === 1 && arcSegIds.has(userSegs[0]);
    };

    let filtered = faces.filter(
        (f) => !faceOverlapsRoundBin(f, markers, segments, userSegmentIds)
    );
    filtered = filtered.filter((f) => graphFaceIsWellBounded(f, userSegmentIds, markers));
    filtered = filtered.filter((f) => {
        if (!faceIsArcWedgeCandidate(f)) return true;
        return faceContainsRequiredArcBulge(f, arcProbes);
    });
    filtered = filtered.filter((f) => !graphFaceOnArcBulgeSide(f, resolvedById));
    /** Replace graph arc wedges with synthesized pockets that follow bin perimeters. */
    filtered = filtered.filter((f) => !faceIsArcWedgeCandidate(f));

    const seenIds = new Set();
    filtered = filtered.filter((f) => {
        if (seenIds.has(f.id)) return false;
        seenIds.add(f.id);
        return true;
    });

    const arcSynth = synthesizeArcPocketFaces(markers, resolvedById, segments);
    filtered = filtered.filter((f) => {
        if (isMultiBinCentralGraphPocket(f, userSegmentIds)) return true;
        if (graphFaceUserWallCount(f, userSegmentIds, resolvedById) >= 2) return true;
        return !graphFaceInsideArcSynth(f, arcSynth);
    });
    arcSynth.forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        const segId = String(sf.id).startsWith("arc:") ? sf.id.slice(4) : null;
        const geom = segId ? resolvedById.get(segId) : null;
        const involved = geom
            ? [geom.markerId1, geom.markerId2].filter(Boolean)
            : [];
        if (
            geom &&
            involved.length &&
            arcPocketIntrudesInvolvedBinInterior(sf.polygon, geom, markers, involved)
        ) {
            return;
        }
        if (!seenIds.has(sf.id)) {
            filtered.push(sf);
            seenIds.add(sf.id);
        }
    });
    filtered = dropGraphArcChannelFills(
        filtered,
        arcSynth,
        userSegmentIds,
        resolvedById
    );
    const rectIdsPresent = new Set(
        filtered
            .map((f) => closedRectIdForFace(f, segments))
            .filter(Boolean)
    );
    synthesizeClosedRectFaces(markers, segments).forEach((sf) => {
        const rectId = String(sf.id).startsWith("flat:") ? sf.id.slice(5) : null;
        if (!rectId || rectIdsPresent.has(rectId)) return;
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        filtered.push(sf);
        rectIdsPresent.add(rectId);
    });

    filtered.forEach((f) => {
        if (!f.labelPoint) f.labelPoint = polygonLabelPoint(f.polygon, markers);
    });
    filtered = filtered.filter(
        (f) => !faceOverlapsRoundBin(f, markers, segments, userSegmentIds)
    );
    filtered = dedupeOverlappingFaces(filtered, userSegmentIds);

    const interiorWallSynth = synthesize2x2InteriorWallRegions(
        markers,
        resolvedById,
        segments
    );
    const wallQuads = interiorWallSynth.filter((f) =>
        String(f.id).startsWith("wallquad:")
    );
    if (wallQuads.length) {
        filtered = dropFacesObscuring2x2WallQuads(filtered, wallQuads);
    }
    interiorWallSynth.forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });
    if (wallQuads.length >= 2) {
        filtered = dropFacesObscuring2x2WallQuads(filtered, wallQuads);
    }

    synthesizeJunctionClosedWallRects(markers, resolvedById).forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    // Bin-perimeter gap pockets follow the round-bin edges exactly; the planar graph
    // cuts straight chords through bin interiors for the same voids. Drop overlapping
    // graph faces FIRST, then add the synthesized pocket so the interior bin gaps win
    // (otherwise the present graph face blocks the synthesized pocket and both vanish).
    const binCentralSynth = synthesize2x2BinCentralPockets(markers, segments);
    filtered = replaceGraphFacesWithBinHubSynth(
        filtered,
        binCentralSynth,
        markers,
        segments,
        userSegmentIds
    );

    const triGapSynth = synthesizeTripleBinGapPockets(markers, segments);
    filtered = replaceGraphFacesWithBinHubSynth(
        filtered,
        triGapSynth,
        markers,
        segments,
        userSegmentIds
    );

    const throatSynth = synthesizeHubThroatPockets(markers, resolvedById);
    throatSynth.forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    const wallRowSynth = synthesize2x2WallRowCentralPockets(markers, segments);
    filtered = replaceGraphFacesWithBinHubSynth(
        filtered,
        wallRowSynth,
        markers,
        segments,
        userSegmentIds
    );

    synthesize2x2ArcCentralPockets(markers, resolvedById, segments).forEach(
        (sf) => {
            if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
            if (wallRowSynth.some((w) => facesOverlap(w, sf) || faceCoveredByBinHubSynth(sf, w))) {
                return;
            }
            if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
            filtered.push(sf);
        }
    );

    wallRowSynth.forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (!filtered.some((f) => f.id === sf.id)) filtered.push(sf);
    });

    synthesizeWallChannelPockets(markers, resolvedById, segments).forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    synthesizeWallBinWedgePockets(markers, resolvedById, segments).forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    synthesizeChannelQuadCornerPockets(markers, resolvedById).forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    synthesizePerimeterCornerPockets(markers, resolvedById).forEach((sf) => {
        if (faceOverlapsRoundBin(sf, markers, segments, userSegmentIds)) return;
        if (
            faceCoreInsideRoundBinMarker(sf, markers) &&
            !faceIsLegitimateMultiBinGap(sf, markers)
        ) {
            return;
        }
        if (filtered.some((f) => f.id === sf.id || facesOverlap(f, sf))) return;
        filtered.push(sf);
    });

    filtered = consolidate2x2BinHubPockets(
        filtered,
        markers,
        segments,
        userSegmentIds
    );

    if (filtered.length > MAX_ENCLOSURE_FACES) {
        filtered = filtered
            .slice()
            .sort((a, b) => a.area - b.area)
            .slice(0, MAX_ENCLOSURE_FACES);
    }

    if (!markers?.length) {
        return commitEnclosureResult(dropExteriorFaceWithoutMarkers(filtered));
    }

    if (filtered.length <= 1) return commitEnclosureResult(filtered);
    /**
     * Drop the exterior face only: a face that contains (almost) every marker center.
     * Equal-area stices inside a regular grid must NOT be filtered out.
     */
    return commitEnclosureResult(
        filtered.filter((f) => {
            let inside = 0;
            markers.forEach((m) => {
                if (pointInPolygon(m.x, m.y, f.polygon)) inside += 1;
            });
            return inside < Math.max(2, markers.length - 1);
        })
    );
};

/**
 * One ordered A,B,C… sequence for stice faces + round-bin markers (top→bottom, left→right).
 */
export const buildOrderedLayoutAreas = (markers, faces) => {
    const seenIds = new Set();
    const uniqueFaces = (faces || []).filter((f) => {
        if (seenIds.has(f.id)) return false;
        seenIds.add(f.id);
        if (faceInteriorInsideRoundBin(f, markers)) return false;
        if (
            faceCoreInsideRoundBinMarker(f, markers) &&
            !faceIsLegitimateMultiBinGap(f, markers)
        ) {
            return false;
        }
        return true;
    });

    const items = [];
    uniqueFaces.forEach((face) => {
        const labelPoint =
            face.labelPoint ||
            (face.polygon?.length
                ? polygonLabelPoint(face.polygon, markers)
                : face.centroid);
        if (!labelPoint) return;
        items.push({
            kind: "face",
            face,
            sortY: labelPoint.y,
            sortX: labelPoint.x,
            labelPoint,
        });
    });
    (markers || []).forEach((m) => {
        items.push({
            kind: "marker",
            marker: m,
            sortY: m.y,
            sortX: m.x,
            labelPoint: { x: m.x, y: m.y },
        });
    });

    items.sort((a, b) => a.sortY - b.sortY || a.sortX - b.sortX);

    items.forEach((item, areaIndex) => {
        item.areaIndex = areaIndex;
        item.letter = unassignedAreaLetter(areaIndex);
        item.areaLabel = `Area ${item.letter}`;
        if (item.kind === "face") {
            item.face.areaOrderIndex = areaIndex;
            item.face.labelPoint = item.labelPoint;
        }
    });

    return items;
};

/** Unassigned area labels: A, B, … Z, AA, AB, … */
export const unassignedAreaLetter = (index) => {
    let n = index;
    let label = "";
    while (n >= 0) {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    }
    return label;
};

/** Stable key for wall-bounded faces (survives enclosure id churn when segment set is unchanged). */
export const faceTopologyKey = (face) =>
    [...(face?.segmentIds || [])].sort().join(",");

/** Label for an enclosed area: assigned bin name, custom name, or capital letter (A, B, …). */
export const getFaceDisplayLabel = (face, index, options = {}) => {
    const { faceLabels = {}, faceAssignments = {}, siteBins = [], sameBinDataId } = options;

    const assignedId = faceAssignments[face.id];
    if (assignedId != null && siteBins.length) {
        const bin = siteBins.find((b) =>
            sameBinDataId ? sameBinDataId(b.binDataId, assignedId) : b.binDataId === assignedId
        );
        if (bin?.binName) return String(bin.binName);
    }

    const custom = faceLabels[face.id];
    if (typeof custom === "string" && custom.trim()) return custom.trim();

    const letterIndex = face.areaOrderIndex ?? index;
    return unassignedAreaLetter(letterIndex);
};

/** Canvas pixel size of an area name tag (matches drawAreaNameTag). */
export const areaNameTagCanvasMetrics = (ctx, label) => {
    const text = String(label ?? "");
    const fontSize = Math.max(11, Math.min(14, 120 / Math.max(text.length, 4)));
    const padX = 6;
    const padY = 3;
    ctx.font = `600 ${fontSize}px Montserrat, sans-serif`;
    const w = ctx.measureText(text).width + padX * 2;
    const h = fontSize + padY * 2;
    return { width: w, height: h };
};

export const areaNameTagCanvasRect = (ctx, label, cx, cy) => {
    const { width, height } = areaNameTagCanvasMetrics(ctx, label);
    return {
        left: cx - width / 2,
        top: cy - height / 2,
        width,
        height,
    };
};

/** Minimum label hit target in image px, scaled to canvas (shrinks when zoomed out). */
const tagHitRectForLabel = (ctx, label, cx, cy, scaleX, scaleY, options = {}) => {
    const canvasScaleMin = Math.min(scaleX, scaleY) || 1;
    const drawn = areaNameTagCanvasRect(ctx, label, cx, cy);
    const minWImage = options.minHitWImage ?? 28;
    const minHImage = options.minHitHImage ?? 18;
    const minW = Math.max(drawn.width, minWImage * canvasScaleMin);
    const minH = Math.max(drawn.height, minHImage * canvasScaleMin);
    const w = Math.max(drawn.width, minW);
    const h = Math.max(drawn.height, minH);
    return {
        left: cx - w / 2,
        top: cy - h / 2,
        width: w,
        height: h,
    };
};

/** Hit-test canvas area labels (P, O, C, …) — tags can sit outside the face polygon. */
export const findFaceTagAtCanvasPoint = (
    canvasX,
    canvasY,
    ctx,
    faces,
    scaleX,
    scaleY,
    options = {}
) => {
    if (!ctx || !faces?.length) return null;
    const canvasScaleMin = Math.min(scaleX, scaleY) || 1;
    const padImage = options.hitPadImage ?? 5;
    const pad = padImage * canvasScaleMin;
    let best = null;
    let bestDist = Infinity;
    faces.forEach((face, i) => {
        const labelPos = face.labelPoint || face.centroid;
        if (!labelPos) return;
        const label = getFaceDisplayLabel(face, face.areaOrderIndex ?? i, options);
        const cx = labelPos.x * scaleX;
        const cy = labelPos.y * scaleY;
        const box = tagHitRectForLabel(ctx, label, cx, cy, scaleX, scaleY, options);
        if (
            canvasX < box.left - pad ||
            canvasX > box.left + box.width + pad ||
            canvasY < box.top - pad ||
            canvasY > box.top + box.height + pad
        ) {
            return;
        }
        const dist = Math.hypot(canvasX - cx, canvasY - cy);
        if (dist < bestDist) {
            bestDist = dist;
            best = face;
        }
    });
    return best;
};

/** Rounded tag at area label position (interior for stices; bin center for round markers). */
export const drawAreaNameTag = (ctx, cx, cy, label, theme) => {
    const { width: w, height: h } = areaNameTagCanvasMetrics(ctx, label);
    const x = cx - w / 2;
    const y = cy - h / 2;

    ctx.fillStyle = theme.tagBg;
    ctx.strokeStyle = theme.tagBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const r = 4;
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = theme.tagText;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, cx, cy);
};

/** @deprecated Use drawAreaNameTag */
export const drawFaceNameTag = drawAreaNameTag;

export const findFaceAtPoint = (x, y, faces) => {
    let best = null;
    let bestDist = Infinity;
    faces.forEach((f) => {
        if (!pointInPolygon(x, y, f.polygon)) return;
        const c =
            f.labelPoint ||
            f.centroid ||
            (f.polygon?.length ? polygonCentroid(f.polygon) : null);
        if (!c) return;
        const d = Math.hypot(x - c.x, y - c.y);
        if (d < bestDist) {
            bestDist = d;
            best = f;
        }
    });
    return best;
};

export const drawEnclosedFaces = (ctx, faces, scaleX, scaleY, options = {}) => {
    const {
        selectedFaceId = null,
        faceAssignments = {},
        faceLabels = {},
        siteBins = [],
        sameBinDataId = null,
        showNameTags = true,
        liteDraw = false,
        maxPolygonPts = 40,
        labelTheme = {
            tagBg: "rgba(255,255,255,0.92)",
            tagBorder: "rgba(0,0,0,0.35)",
            tagText: "#1a1a1a",
        },
        colors = ["rgba(233,30,99,0.18)", "rgba(156,39,176,0.18)", "rgba(0,188,212,0.18)"],
    } = options;

    const drawPoly =
        liteDraw && maxPolygonPts > 0 ? (poly) => decimatePolygonForDraw(poly, maxPolygonPts) : (poly) => poly;

    faces.forEach((face, i) => {
        const assigned = faceAssignments[face.id];
        const selected = face.id === selectedFaceId;
        if (liteDraw && !selected && !assigned) return;

        const poly = drawPoly(face.polygon);
        if (!poly?.length) return;

        ctx.beginPath();
        poly.forEach((p, pi) => {
            const cx = p.x * scaleX;
            const cy = p.y * scaleY;
            if (pi === 0) ctx.moveTo(cx, cy);
            else ctx.lineTo(cx, cy);
        });
        ctx.closePath();
        if (selected) {
            ctx.fillStyle = "rgba(255,235,59,0.35)";
        } else if (assigned) {
            ctx.fillStyle = "rgba(76,175,80,0.28)";
        } else {
            ctx.fillStyle = colors[(face.areaOrderIndex ?? i) % colors.length];
        }
        ctx.fill();
        if (selected) {
            ctx.strokeStyle = "#ffeb3b";
            ctx.lineWidth = 2;
            ctx.stroke();
        }

        const labelPos = face.labelPoint || face.centroid;
        if (showNameTags && labelPos) {
            const label = getFaceDisplayLabel(face, face.areaOrderIndex ?? i, {
                faceLabels,
                faceAssignments,
                siteBins,
                sameBinDataId,
            });
            const theme = selected
                ? {
                      tagBg: "rgba(255,235,59,0.95)",
                      tagBorder: "#f57f17",
                      tagText: "#1a1a1a",
                  }
                : assigned
                    ? {
                          tagBg: "rgba(200,230,201,0.95)",
                          tagBorder: "#388e3c",
                          tagText: "#1b5e20",
                      }
                    : labelTheme;
            drawAreaNameTag(
                ctx,
                labelPos.x * scaleX,
                labelPos.y * scaleY,
                label,
                theme
            );
        }
    });
};

export const flattenLayoutSegments = (stices) => {
    const out = [];
    (stices || []).forEach((s) => {
        (s.segments || []).forEach((seg) => out.push(seg));
    });
    return out;
};

export const migrateSticeAssignmentsToFaces = (stices, faces) => {
    const assignments = {};
    if (!stices?.length || !faces?.length) return assignments;
    stices.forEach((stice) => {
        if (stice.binDataId == null) return;
        const segIds = new Set((stice.segments || []).map((s) => s.id));
        let best = null;
        let bestOverlap = 0;
        faces.forEach((face) => {
            const overlap = face.segmentIds.filter((id) => segIds.has(id)).length;
            if (overlap > bestOverlap) {
                bestOverlap = overlap;
                best = face;
            }
        });
        if (best && bestOverlap > 0) assignments[best.id] = stice.binDataId;
    });
    return assignments;
};
